import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

const API_BASE =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

// Internal training/demo client used for screenshots and tutorial videos.
// This stays entirely client-side so it never touches a real restaurant record.
const FOURTH_DOWN_DEMO_TOKEN = "demo-fourth-down";

const FOURTH_DOWN_DEMO_CLIENT = {
  slug: "fourth-down-tavern",
  restaurant_name: "Fourth Down Tavern",
  description:
    "Game day wings, burgers, cold beer, and football specials in Pittsburgh.",
  offer: "Game Day Wings & Pitcher Deal",
  cta_text: "Claim Game Day Offer",
  cta_link: "",
  after_claim_button_text: "View Menu",
  after_claim_button_link: "",
  campaign_type: "offer",
  event_name: "",
  event_date: "",
  event_time: "",
  event_description: "",
  event_action_text: "View Event Details",
  event_action_link: "",
  offer_terms: "Valid on game day · Dine-in only · One offer per table",
  phone: "+1-412-555-0194",
  address: "1840 Carson Street, Pittsburgh, PA",
  website_url: "https://fourthdowntavern.com",
  menu_url: "https://fourthdowntavern.com/menu",
  hero_image_url: "/demo/fourth-down/fourth-down-game-day.png",
  hours:
    "Monday: 4:00 PM – 10:00 PM\nTuesday: 4:00 PM – 10:00 PM\nWednesday: 4:00 PM – 10:00 PM\nThursday: 4:00 PM – 10:00 PM\nFriday: 4:00 PM – 11:00 PM\nSaturday: 12:00 PM – 11:00 PM\nSunday: 12:00 PM – 10:00 PM",
  theme: "bar",
  brand_color: "#D4AF37",
  recommended_brand_color: "#D4AF37",
  rating: 4.7,
  review_count: 286,
  page_visits: 1248,
  offer_claims: 186,
  event_actions: 0,
  success_reminders_enabled: true,
  success_reminder_email_enabled: true,
  success_reminder_cadence: "weekly",
  success_reminder_day: "Thursday",
  success_reminder_last_sent_at: "",
  success_reminder_next_at: "",
  success_reminder_unsubscribed_at: "",
};

const HERO_IMAGE_ACCEPTED_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
]);

const HERO_IMAGE_MAX_BYTES = 10 * 1024 * 1024;

const OFFER_OPTIONS_BY_THEME = {
  pizza: ["Free Garlic Knots with Any Pizza", "10% Off Any Large Pizza", "Free Drink with Two Slices"],
  bbq: ["Free Side with Any BBQ Platter", "10% Off Family Packs", "Free Drink with Any BBQ Sandwich"],
  sushi: ["Free Appetizer with Any Two Rolls", "10% Off Orders of $40 or More", "Free Drink with Any Lunch Roll Combo"],
  seafood: ["Free Appetizer with Any Two Entrées", "10% Off Dinner for Two", "Free Drink with Any Lunch Entrée"],
  cafe: ["Free Pastry with Any Large Coffee", "Free Cookie with Any Specialty Drink", "Free Coffee with Any Breakfast Entrée"],
  bakery: ["Free Coffee with Any Pastry", "Free Cookie with Purchase", "Buy One Pastry, Get One 50% Off"],
  tacos: ["Free Chips and Salsa with Any Two Entrées", "10% Off Orders of $40 or More", "Free Drink with Any Taco Combo"],
  bar: ["Free Appetizer with Two Entrées", "Free Dessert with Two Entrées", "Free Drink with Any Entrée", "Free Appetizer with Any Table Order"],
  default: ["Free Appetizer with Purchase", "10% Off Your First Visit", "Free Dessert with Purchase"],
};

const BUTTON_OPTIONS_BY_THEME = {
  pizza: ["Claim Pizza Deal", "Claim My Pizza Offer", "Get This Pizza Deal"],
  bbq: ["Claim BBQ Deal", "Claim My BBQ Offer", "Get This BBQ Deal"],
  sushi: ["Claim Sushi Deal", "Claim My Sushi Offer", "Get This Sushi Deal"],
  seafood: ["Claim Seafood Deal", "Claim My Seafood Offer", "Get This Seafood Deal"],
  cafe: ["Claim Coffee Deal", "Claim My Cafe Offer", "Get This Deal"],
  bakery: ["Claim Bakery Deal", "Claim My Bakery Offer", "Get This Deal"],
  tacos: ["Claim Taco Deal", "Claim My Taco Offer", "Get This Deal"],
  bar: ["Claim Happy Hour Deal", "Claim My Offer", "Get This Deal"],
  default: ["Claim My Offer", "Get This Deal", "Claim Special"],
};


const EVENT_HOURS = Array.from({ length: 12 }, (_, index) =>
  String(index + 1)
);
const EVENT_MINUTES = ["00", "15", "30", "45"];
const EVENT_MERIDIEMS = ["AM", "PM"];

function parseEventTime(value = "") {
  const fallback = {
    startHour: "6",
    startMinute: "00",
    startMeridiem: "PM",
    endHour: "7",
    endMinute: "00",
    endMeridiem: "PM",
  };

  const match = String(value).match(
    /(\d{1,2}):(\d{2})\s*(AM|PM)\s*[–—-]\s*(\d{1,2}):(\d{2})\s*(AM|PM)/i
  );

  if (!match) return fallback;

  return {
    startHour: String(Number(match[1]) || 6),
    startMinute: match[2],
    startMeridiem: match[3].toUpperCase(),
    endHour: String(Number(match[4]) || 7),
    endMinute: match[5],
    endMeridiem: match[6].toUpperCase(),
  };
}

function updateEventTime(value = "", key, nextValue) {
  const parts = {
    ...parseEventTime(value),
    [key]: nextValue,
  };

  return `${parts.startHour}:${parts.startMinute} ${parts.startMeridiem} – ${parts.endHour}:${parts.endMinute} ${parts.endMeridiem}`;
}

const EVENT_ACTION_OPTIONS = [
  "View Event Details",
  "RSVP",
  "Reserve Table",
  "Get Tickets",
  "Join Guest List",
  "Save My Spot",
  "See Event Details",
  "Plan My Visit",
  "Custom...",
];

const EVENT_DESTINATION_OPTIONS = [
  { value: "website", label: "Restaurant Website", description: "Uses the website saved in Restaurant Info automatically." },
  { value: "menu", label: "Menu", description: "Uses the menu link saved in Restaurant Info automatically." },
  { value: "reservation", label: "Reservation Page", description: "Paste the restaurant's reservation or booking page." },
  { value: "tickets_rsvp", label: "Ticket / RSVP / Guest List Page", description: "Paste the page customers use to RSVP, join a guest list, or get tickets." },
  { value: "event_details", label: "Event Details / Facebook Event", description: "Paste the event details page or Facebook Event link." },
  { value: "instagram", label: "Instagram Profile", description: "Paste the restaurant's Instagram profile link." },
  { value: "custom", label: "Custom Link", description: "Paste any other page you want this button to open." },
  { value: "none", label: "No Link Yet", description: "The button remains visible, but it will not open another page yet." },
];

function normalizeExternalUrl(value = "") {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";
  if (/^(https?:|mailto:|tel:|sms:)/i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  return `https://${trimmed.replace(/^\/+/, "")}`;
}

function urlsMatch(first = "", second = "") {
  const a = normalizeExternalUrl(first).replace(/\/$/, "");
  const b = normalizeExternalUrl(second).replace(/\/$/, "");
  return Boolean(a && b && a.toLowerCase() === b.toLowerCase());
}

function inferEventDestinationType(form = {}) {
  const eventLink = form.event_action_link || "";
  if (eventLink && urlsMatch(eventLink, form.website_url)) return "website";
  if (eventLink && urlsMatch(eventLink, form.menu_url)) return "menu";
  const action = resolvedEventActionText(form);
  if (action === "Reserve Table") return "reservation";
  if (["RSVP", "Get Tickets", "Join Guest List", "Save My Spot"].includes(action)) return "tickets_rsvp";
  if (["View Event Details", "See Event Details", "Plan My Visit"].includes(action)) return eventLink ? "event_details" : "none";
  return eventLink ? "custom" : "none";
}

function eventDestinationLink(form = {}, destinationType = "custom") {
  if (destinationType === "website") return normalizeExternalUrl(form.website_url);
  if (destinationType === "menu") return normalizeExternalUrl(form.menu_url);
  if (destinationType === "none") return "";
  return normalizeExternalUrl(form.event_action_link);
}

function eventDestinationPlaceholder(destinationType = "custom") {
  switch (destinationType) {
    case "reservation": return "restaurant.com/reservations";
    case "tickets_rsvp": return "eventbrite.com/... or restaurant.com/rsvp";
    case "event_details": return "facebook.com/events/... or restaurant.com/events";
    case "instagram": return "instagram.com/restaurant";
    default: return "www.site.com/page";
  }
}

const EVENT_NAME_IDEAS = [
  "Live Music Night",
  "Trivia Night",
  "Game / Watch Party",
  "Tasting Event",
  "Holiday Event",
  "Family Night",
  "Guest Chef Night",
  "Special Dinner",
  "Community Event",
  "Catering Showcase",
];

const CLIENT_COLOR_PRESETS = [
  ["Cyan", "#00E5FF"],
  ["Red", "#E63946"],
  ["Orange", "#F97316"],
  ["Gold", "#D4AF37"],
  ["Lime", "#84CC16"],
  ["Green", "#2E7D32"],
  ["Teal", "#0F9D8A"],
  ["Blue", "#0288D1"],
  ["Navy", "#1E3A8A"],
  ["Purple", "#6D3CCB"],
  ["Violet", "#8B5CF6"],
  ["Pink", "#EC4899"],
  ["Rose", "#E11D48"],
  ["Brown", "#8B5E3C"],
  ["Burgundy", "#7F1D1D"],
  ["Slate", "#64748B"],
];

const CLIENT_THEME_COLORS = {
  default: "#00E5FF",
  pizza: "#E63946",
  burger: "#F4B400",
  tacos: "#2E7D32",
  cafe: "#6F4E37",
  italian: "#C62828",
  fine_dining: "#D4AF37",
  bbq: "#B3541E",
  seafood: "#0288D1",
  cajun: "#D84315",
  chicken: "#E6A700",
  chinese: "#B71C1C",
  sushi: "#5E35B1",
  icecream: "#EC4899",
  bakery: "#C68642",
  hotdog: "#C62828",
};

function customerThemeColor(form = {}) {
  const custom = String(form.brand_color || "").trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(custom)) return custom;

  return "#00E5FF";
}

function colorNameFor(value = "") {
  const normalized = String(value || "").trim().toLowerCase();
  const match = CLIENT_COLOR_PRESETS.find(
    ([, color]) => color.toLowerCase() === normalized
  );
  return match ? match[0] : "Custom";
}

function recommendedColor(form = {}) {
  const recommended = String(form.recommended_brand_color || "").trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(recommended)) return recommended;
  return customerThemeColor(form);
}

function contrastTextColor(hex = "#00E5FF") {
  const normalized = String(hex || "").replace("#", "");
  if (!/^[0-9A-Fa-f]{6}$/.test(normalized)) return "#09090B";

  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;

  return brightness >= 150 ? "#09090B" : "#FFFFFF";
}

function resolvedEventActionText(form = {}) {
  return form.event_action_text === "Custom..."
    ? form.custom_event_action_text || "View Event Details"
    : form.event_action_text || "View Event Details";
}


const AFTER_CLAIM_OPTIONS = [
  "View Menu",
  "Get Directions",
  "Visit Website",
  "Order Online",
  "Reserve Table",
  "Join Rewards",
  "Custom...",
];

function detectClientTheme(form = {}) {
  const blob = [form.restaurant_name, form.description, form.offer]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (/\bpizza\b|\bpizzeria\b/.test(blob)) return "pizza";
  if (/\bbbq\b|\bbarbecue\b|\bsmokehouse\b|\bbrisket\b/.test(blob)) return "bbq";
  if (/\bsushi\b|\bjapanese\b|\bramen\b/.test(blob)) return "sushi";
  if (/\bseafood\b|\boyster\b|\blobster\b|\bcrab\b/.test(blob)) return "seafood";
  if (/\bcafe\b|\bcoffee\b|\bespresso\b|\bbrunch\b/.test(blob)) return "cafe";
  if (/\bbakery\b|\bpastry\b|\bcroissant\b|\bdonut\b/.test(blob)) return "bakery";
  if (/\btaco\b|\bmexican\b|\bburrito\b|\btaqueria\b/.test(blob)) return "tacos";
  if (/\bbar\b|\bpub\b|\bcocktail\b|\btavern\b/.test(blob)) return "bar";
  return "default";
}

function offerChoicesFor(form = {}) {
  const options = OFFER_OPTIONS_BY_THEME[detectClientTheme(form)] || OFFER_OPTIONS_BY_THEME.default;
  return Array.from(new Set([form.custom_offer || form.offer, ...options].filter(Boolean)));
}

function buttonChoicesFor(form = {}) {
  const options =
    BUTTON_OPTIONS_BY_THEME[detectClientTheme(form)] ||
    BUTTON_OPTIONS_BY_THEME.default;

  const universalOptions = [
    "Claim My Offer",
    "Get This Deal",
    "Claim Special",
    "See the Special",
    "Get the Details",
    "Plan My Visit",
    "View Menu",
    "Order Online",
    "Reserve Table",
  ];

  return Array.from(
    new Set(
      [
        form.custom_cta_text || form.cta_text,
        ...options,
        ...universalOptions,
      ].filter(Boolean)
    )
  );
}

function afterClaimNeedsLink(action = "") {
  return ["Order Online", "Reserve Table", "Join Rewards", "Custom..."].includes(action);
}

function promotionIntentText(form = {}) {
  return [
    form.offer,
    form.custom_offer,
    form.description,
    form.cta_text,
    form.custom_cta_text,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function isVisitOrientedPromotion(form = {}) {
  if ((form.campaign_type || "offer") === "event") return false;

  const text = promotionIntentText(form);

  return [
    "stop in",
    "visit",
    "dine-in",
    "dine in",
    "happy hour",
    "midweek",
    "weekend",
    "lunch",
    "dinner",
    "family",
    "tonight",
    "this week",
    "table",
    "in-store",
    "in store",
  ].some((phrase) => text.includes(phrase));
}

function isOrderingOrTakeoutPromotion(form = {}) {
  const text = promotionIntentText(form);

  return [
    "order online",
    "online order",
    "takeout",
    "take-out",
    "pickup",
    "pick-up",
    "delivery",
    "to-go",
    "to go",
  ].some((phrase) => text.includes(phrase));
}

function hasDedicatedOrderLink(form = {}) {
  const link = String(
    form.after_claim_button_link || form.cta_link || ""
  ).trim();

  if (!link) return false;

  if (form.website_url && urlsMatch(link, form.website_url)) {
    return false;
  }

  return true;
}

function recommendedAfterClaimAction(form = {}) {
  if ((form.campaign_type || "offer") === "event") {
    return form.after_claim_button_text || "View Menu";
  }

  if (
    isOrderingOrTakeoutPromotion(form) &&
    hasDedicatedOrderLink(form)
  ) {
    return "Order Online";
  }

  if (isVisitOrientedPromotion(form)) {
    if (form.menu_url) return "View Menu";
    if (form.address) return "Get Directions";
    if (form.website_url) return "Visit Website";
  }

  if (form.menu_url) return "View Menu";
  if (form.address) return "Get Directions";
  if (form.website_url) return "Visit Website";

  return "Custom...";
}

function shouldRepairGeneratedAfterClaim(form = {}) {
  if ((form.campaign_type || "offer") === "event") return false;

  const action = String(form.after_claim_button_text || "").trim();

  if (!action) return true;

  if (
    action === "Order Online" &&
    isVisitOrientedPromotion(form) &&
    !hasDedicatedOrderLink(form)
  ) {
    return true;
  }

  return false;
}

function resolvedAfterClaimText(form = {}) {
  return form.after_claim_button_text === "Custom..."
    ? form.custom_after_claim_button_text || "Continue"
    : form.after_claim_button_text || recommendedAfterClaimAction(form);
}

function resolvedAfterClaimLink(form = {}) {
  const action = resolvedAfterClaimText(form);

  if (action === "View Menu") return form.menu_url || "";

  if (action === "Get Directions") {
    return form.address
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(form.address)}`
      : "";
  }

  if (action === "Visit Website") return form.website_url || "";

  return String(form.after_claim_button_link || "").trim();
}

function automaticDestinationMessage(form = {}) {
  const action = resolvedAfterClaimText(form);

  if (action === "View Menu") {
    return form.menu_url
      ? `Uses your menu link automatically: ${form.menu_url}`
      : "Add your menu link in Restaurant Info so this action can open it.";
  }

  if (action === "Get Directions") {
    return form.address
      ? `Uses your restaurant address automatically: ${form.address}`
      : "Add your restaurant address in Restaurant Info so this action can open directions.";
  }

  if (action === "Visit Website") {
    return form.website_url
      ? `Uses your restaurant website automatically: ${form.website_url}`
      : "Add your website in Restaurant Info so this action can open it.";
  }

  return "";
}

function afterClaimActionWarning(form = {}) {
  const action = resolvedAfterClaimText(form);

  if (
    action === "Order Online" &&
    isVisitOrientedPromotion(form) &&
    !hasDedicatedOrderLink(form)
  ) {
    return "This promotion is written to drive an in-person visit, but Order Online does not have a dedicated ordering link. View Menu or Get Directions is a better fit.";
  }

  if (
    action === "Order Online" &&
    form.after_claim_button_link &&
    form.website_url &&
    urlsMatch(form.after_claim_button_link, form.website_url)
  ) {
    return "Order Online currently points to your homepage. Use your real ordering page, or choose View Menu / Get Directions instead.";
  }

  return "";
}

function automaticUtilityButtons(form = {}) {
  const buttons = [];
  if (form.website_url) buttons.push({ label: "Website", href: form.website_url });
  if (form.phone) buttons.push({ label: "Call", href: `tel:${String(form.phone).replace(/[^+\\d]/g, "")}` });
  if (form.address) {
    buttons.push({
      label: "Directions",
      href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(form.address)}`,
    });
  }
  return buttons;
}

const OFFER_TERMS_OPTIONS = [
  "Valid through Sunday · One per customer · Not combinable with other offers",
  "Valid this week only · One per table · Dine-in only",
  "Valid through the end of the month · One per customer",
  "Mention this offer when ordering · Cannot be combined with other discounts",
  "Dine-in only · One offer per table",
  "Takeout only · Must mention offer when ordering",
  "Valid during regular business hours · While supplies last",
  "Not valid with third-party delivery apps",
  "Custom...",
];

const PROMOTION_MESSAGE_IDEAS = [
  "Stop in today and enjoy a special offer made to give you one more reason to visit.",
  "Planning your next meal? Claim this offer before you visit and make your next stop even better.",
  "Hungry? Tap below to claim today’s featured offer and show it when you visit.",
  "Treat yourself this week with a limited-time promotion available while it lasts.",
  "Bring your appetite — we have something special waiting for you on your next visit.",
  "Make your next visit count. Claim this offer now and enjoy something extra when you stop in.",
  "Looking for a reason to visit today? Our current promotion is ready when you are.",
  "Share a meal, try something new, and claim this special offer before your next visit.",
];


const SUCCESS_REMINDER_CADENCE_OPTIONS = [
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Every 2 Weeks" },
  { value: "monthly", label: "Monthly" },
];

const SUCCESS_REMINDER_DAY_OPTIONS = [
  "Monday", "Tuesday", "Wednesday", "Thursday",
  "Friday", "Saturday", "Sunday",
];

function formatReminderDate(value = "") {
  if (!value) return "Not scheduled yet";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Not scheduled yet";
  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function successCenterResourceUrl(resource = {}, form = {}) {
  const rawUrl = String(resource.url || "").trim();
  const title = String(resource.title || "").trim().toLowerCase();
  const isTapToTableSupport =
    rawUrl.toLowerCase().startsWith("mailto:support@taptotable.co") ||
    title === "taptotable support";

  if (!isTapToTableSupport) return rawUrl;

  const restaurantName =
    String(form.restaurant_name || "").trim() || "Restaurant";
  const livePageUrl = form.slug
    ? `https://taptotable.co/r/${form.slug}`
    : "https://taptotable.co";

  const subject = `TapToTable Support - ${restaurantName}`;
  const body = [
    "Hi TapToTable Support,",
    "",
    `Restaurant: ${restaurantName}`,
    `TapToTable Page: ${livePageUrl}`,
    "",
    "Please describe what you need help with:",
    "",
  ].join("\n");

  return `mailto:support@taptotable.co?subject=${encodeURIComponent(
    subject
  )}&body=${encodeURIComponent(body)}`;
}

const UNIVERSAL_PROMOTION_COPY = {
  "Ready-Made Promotion Lines": [
    "This week at [RESTAURANT]: enjoy [FEATURED ITEM OR SPECIAL]. Tap below for details and make plans to visit.",
    "Looking for a reason to stop in? We’re featuring [MENU ITEM OR EXPERIENCE] for a limited time.",
    "Make your next visit count. Stop by [RESTAURANT] for [PROMOTION OR FEATURED ITEM].",
    "Now featured at [RESTAURANT]: [MENU ITEM / SPECIAL / EXPERIENCE].",
    "Treat yourself this week at [RESTAURANT]. Tap below to see what we’re featuring.",
    "A new reason to visit [RESTAURANT]: [PROMOTION OR FEATURED ITEM].",
  ],
  "Urgency Lines": [
    "This week only.",
    "This weekend only.",
    "Tonight only.",
    "Available while supplies last.",
    "Limited availability.",
    "Available through [DATE].",
    "For a limited time.",
    "Don’t miss it.",
  ],
  "Promotion Button Ideas": [
    "Claim My Offer",
    "Get This Deal",
    "View Menu",
    "Order Online",
    "Reserve Table",
    "See the Special",
    "Get the Details",
    "Plan My Visit",
  ],
  "Offer Terms / Fine Print": [
    "Valid through [DATE].",
    "One per customer.",
    "One per table.",
    "Dine-in only.",
    "While supplies last.",
    "Not combinable with other offers.",
    "Mention this promotion when ordering.",
    "Available during regular business hours.",
  ],
};

const UNIVERSAL_EVENT_COPY = {
  "Ready-Made Event Lines": [
    "Join us at [RESTAURANT] for [EVENT NAME] on [DATE] from [TIME]. Tap below for details and make your plans.",
    "[EVENT NAME] is coming to [RESTAURANT]. Save the date for [DATE] and tap below for details.",
    "Make plans to join us for [EVENT NAME] at [RESTAURANT] on [DATE].",
    "Something special is happening at [RESTAURANT]: [EVENT NAME] on [DATE].",
    "Join us for [EVENT NAME] at [RESTAURANT]. Tap below to reserve, RSVP, or get the details.",
    "Mark your calendar: [EVENT NAME] at [RESTAURANT] on [DATE] at [TIME].",
  ],
  "Event Urgency Lines": [
    "Reservations recommended.",
    "Limited seating — reserve early.",
    "Limited availability.",
    "Space is limited.",
    "RSVP by [DATE].",
    "Tickets available while they last.",
    "Doors open at [TIME].",
    "Save the date.",
  ],
  "Event Button Ideas": [
    "View Event Details",
    "RSVP",
    "Reserve Table",
    "Get Tickets",
    "Join Guest List",
    "Save My Spot",
    "See Event Details",
    "Plan My Visit",
  ],
  "Event Detail Lines": [
    "Date: [DATE]",
    "Time: [TIME]",
    "Location: [RESTAURANT / ADDRESS]",
    "Reservations recommended.",
    "Walk-ins welcome.",
    "Family-friendly event.",
    "Must be 21+ to attend.",
    "Contact us for more information.",
  ],
};

export default function ClientPortal() {
  const { token } = useParams();
  const isFourthDownDemo = token === FOURTH_DOWN_DEMO_TOKEN;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false);
  const [previewScreen, setPreviewScreen] = useState("primary");
  const [eventDestinationType, setEventDestinationType] = useState("none");
  const [heroUploading, setHeroUploading] = useState(false);
  const [heroDragActive, setHeroDragActive] = useState(false);
  const [heroUploadMessage, setHeroUploadMessage] = useState("");
  const [promotionCopyType, setPromotionCopyType] = useState(
    "Ready-Made Promotion Lines"
  );
  const [eventCopyType, setEventCopyType] = useState(
    "Ready-Made Event Lines"
  );
  const [copiedResourceKey, setCopiedResourceKey] = useState("");

  const [successCenter, setSuccessCenter] = useState(null);
  const [snoozingReminder, setSnoozingReminder] = useState(false);
  const [reminderActionMessage, setReminderActionMessage] = useState("");

 const [form, setForm] = useState({
  slug: "",
  restaurant_name: "",
  description: "",
  offer: "",
  cta_text: "",
  cta_link: "",
  after_claim_button_text: "",
  after_claim_button_link: "",
  campaign_type: "offer",
   
  featured_item_name: "",
  featured_item_description: "",
  featured_action_text: "View Menu",
  featured_action_link: "",

  event_name: "",
  event_date: "",
  event_time: "",
  event_description: "",
  event_action_text: "View Event Details",
  event_action_link: "",
  custom_event_action_text: "",
  offer_terms: "Valid this week only · One per table · Not combinable with other offers",
  phone: "",
  address: "",
  website_url: "",
  menu_url: "",
  hero_image_url: "",
  hours: "",
  theme: "default",
  brand_color: "#00E5FF",
  recommended_brand_color: "",
  rating: null,
  review_count: 0,
  page_visits: 0,
  offer_claims: 0,
  event_actions: 0,
  success_reminders_enabled: false,
  success_reminder_email_enabled: true,
  success_reminder_cadence: "biweekly",
  success_reminder_day: "Monday",
  success_reminder_last_sent_at: "",
  success_reminder_next_at: "",
  success_reminder_unsubscribed_at: "",
});

  useEffect(() => {
  async function run() {
    await loadPortal();
  }

  run();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  async function loadPortal() {
    try {
      setLoading(true);

      if (isFourthDownDemo) {
        const data = FOURTH_DOWN_DEMO_CLIENT;

        try {
          const successCenterRes = await fetch(`${API_BASE}/api/success-center`);
          if (successCenterRes.ok) {
            setSuccessCenter(await successCenterRes.json());
          }
        } catch (successCenterErr) {
          console.warn("Demo Success Center load skipped:", successCenterErr);
        }

        setEventDestinationType(
          inferEventDestinationType({
            ...data,
            event_action_text: data.event_action_text || "View Event Details",
          })
        );

        const loadedAfterClaimAction = shouldRepairGeneratedAfterClaim(data)
          ? recommendedAfterClaimAction(data)
          : AFTER_CLAIM_OPTIONS.includes(data.after_claim_button_text)
            ? data.after_claim_button_text
            : data.after_claim_button_text
              ? "Custom..."
              : recommendedAfterClaimAction(data);

        const loadedAfterClaimLink =
          ["View Menu", "Get Directions", "Visit Website"].includes(
            loadedAfterClaimAction
          )
            ? ""
            : data.after_claim_button_link || data.cta_link || "";

        setForm({
          ...data,
          custom_offer: "",
          custom_cta_text: "",
          custom_event_action_text: "",
          after_claim_button_text: loadedAfterClaimAction,
          custom_after_claim_button_text: "",
          after_claim_button_link: loadedAfterClaimLink,
        });

        return;
      }

      const res = await fetch(
        `${API_BASE}/api/mockups/client-portal/${token}`
      );

      if (!res.ok) {
        throw new Error("Could not load TapToTable Dashboard");
      }

      const data = await res.json();

const successCenterRes = await fetch(
  `${API_BASE}/api/success-center`
);

if (!successCenterRes.ok) {
  throw new Error("Could not load Success Center");
}

const successCenterData = await successCenterRes.json();

setSuccessCenter(successCenterData);

setEventDestinationType(
  inferEventDestinationType({
    ...data,
    event_action_text: data.event_action_text || "View Event Details",
  })
);

const loadedAfterClaimAction = shouldRepairGeneratedAfterClaim(data)
  ? recommendedAfterClaimAction(data)
  : AFTER_CLAIM_OPTIONS.includes(data.after_claim_button_text)
    ? data.after_claim_button_text
    : data.after_claim_button_text
      ? "Custom..."
      : recommendedAfterClaimAction(data);

const loadedAfterClaimLink =
  ["View Menu", "Get Directions", "Visit Website"].includes(
    loadedAfterClaimAction
  )
    ? ""
    : data.after_claim_button_link || data.cta_link || "";

setForm({
        slug: data.slug || "",
        restaurant_name: data.restaurant_name || "",
        description: data.description || "",
        offer: data.offer || "",
        custom_offer: "",
        cta_text: data.cta_text || "",
        custom_cta_text: "",
        cta_link: data.cta_link || "",
        phone: data.phone || "",
        address: data.address || "",
        website_url: data.website_url || "",
        menu_url: data.menu_url || "",
        hero_image_url: data.hero_image_url || "",
        hours: data.hours || "",
        theme: data.theme || "default",
        brand_color: data.brand_color || "#00E5FF",
        recommended_brand_color:
          data.recommended_brand_color || data.brand_color || "#00E5FF",
        rating:
          data.rating === null || data.rating === undefined
            ? null
            : Number(data.rating),
        review_count: Number(
          data.review_count ?? data.rating_count ?? 0
        ),
        page_visits: data.page_visits || 0,
        offer_claims: data.offer_claims || 0,
        event_actions: data.event_actions || 0,
        campaign_type: data.campaign_type || "offer",
  
        featured_item_name: data.featured_item_name || "",
        featured_item_description: data.featured_item_description || "",
        featured_action_text: data.featured_action_text || "View Menu",
        featured_action_link: data.featured_action_link || "",

        event_name: data.event_name || "",
        event_date: data.event_date || "",
        event_time: data.event_time || "",
        event_description: data.event_description || "",
        event_action_text: EVENT_ACTION_OPTIONS.includes(
          data.event_action_text
        )
          ? data.event_action_text
          : data.event_action_text
            ? "Custom..."
            : "View Event Details",
        custom_event_action_text:
          data.event_action_text &&
          !EVENT_ACTION_OPTIONS.includes(data.event_action_text)
            ? data.event_action_text
            : "",
        event_action_link: data.event_action_link || "",
        after_claim_button_text: loadedAfterClaimAction,
        custom_after_claim_button_text:
          data.after_claim_button_text &&
          !AFTER_CLAIM_OPTIONS.includes(data.after_claim_button_text)
            ? data.after_claim_button_text
            : "",
        after_claim_button_link: loadedAfterClaimLink,
        offer_terms:
          data.offer_terms ||
          "Valid this week only · One per table · Not combinable with other offers",
        success_reminders_enabled: Boolean(data.success_reminders_enabled),
        success_reminder_email_enabled: data.success_reminder_email_enabled !== false,
        success_reminder_cadence: data.success_reminder_cadence || "biweekly",
        success_reminder_day: data.success_reminder_day || "Monday",
        success_reminder_last_sent_at: data.success_reminder_last_sent_at || "",
        success_reminder_next_at: data.success_reminder_next_at || "",
        success_reminder_unsubscribed_at: data.success_reminder_unsubscribed_at || "",
      });
    } catch (err) {
      console.error(err);
      setError("Failed to load TapToTable Dashboard.");
    } finally {
      setLoading(false);
    }
  }

  function updateField(key, value) {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  }

  function scrollToHelp() {
    document
      .getElementById("client-help-resources")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function scrollToCopyIdeas() {
    document
      .getElementById("client-copy-paste-ideas")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function jumpToSection(sectionId) {
    document
      .getElementById(sectionId)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function jumpToQuickEdit() {
    jumpToSection("client-quick-edit");
  }

  async function remindMeLater() {
    if (snoozingReminder) return;

    try {
      setSnoozingReminder(true);
      setReminderActionMessage("");

      if (isFourthDownDemo) {
        const next = new Date();
        next.setDate(next.getDate() + 7);

        setForm((prev) => ({
          ...prev,
          success_reminder_next_at: next.toISOString(),
        }));

        setReminderActionMessage(
          "Demo saved — the next reminder was moved ahead by 7 days."
        );
        return;
      }

      const res = await fetch(
        `${API_BASE}/api/mockups/client-portal/${token}/success-reminders/snooze`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            days: 7,
          }),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          data?.detail || "Could not snooze this reminder."
        );
      }

      setForm((prev) => ({
        ...prev,
        success_reminder_next_at:
          data.success_reminder_next_at ||
          prev.success_reminder_next_at,
      }));

      setReminderActionMessage(
        "Saved automatically — we’ll remind you again in 7 days. No need to click Save Reminder Preferences."
      );
    } catch (err) {
      console.error("Success Reminder snooze failed:", err);
      setReminderActionMessage(
        err?.message || "Could not snooze this reminder."
      );
    } finally {
      setSnoozingReminder(false);
    }
  }

  async function copyResourceText(value, key) {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedResourceKey(key);
      window.setTimeout(() => {
        setCopiedResourceKey((current) => (current === key ? "" : current));
      }, 1800);
    } catch (err) {
      console.error("Copy resource text failed:", err);
      alert("Could not copy. Select the text and copy it manually.");
    }
  }

  async function handleHeroImageFile(file) {
    if (!file || heroUploading) return;

    setHeroUploadMessage("");

    if (!HERO_IMAGE_ACCEPTED_TYPES.has(file.type)) {
      setHeroUploadMessage("Use a JPG, PNG, WEBP, or GIF image.");
      return;
    }

    if (file.size > HERO_IMAGE_MAX_BYTES) {
      setHeroUploadMessage("Image must be 10 MB or smaller.");
      return;
    }

    if (isFourthDownDemo) {
      const localPreviewUrl = URL.createObjectURL(file);
      updateField("hero_image_url", localPreviewUrl);
      setHeroUploadMessage(
        "Demo image loaded for this session. Click Save Changes to continue the tutorial."
      );
      return;
    }

    setHeroUploading(true);

    try {
      const signRes = await fetch(
        `${API_BASE}/api/mockups/client-portal/${token}/media-upload-url`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            filename: file.name || "restaurant-hero",
            content_type: file.type,
            purpose: "hero",
          }),
        }
      );

      const signData = await signRes.json().catch(() => ({}));

      if (!signRes.ok) {
        throw new Error(
          signData?.detail || "Could not prepare image upload."
        );
      }

      const uploadRes = await fetch(signData.upload_url, {
        method: "PUT",
        headers: {
          "Content-Type": signData.content_type || file.type,
        },
        body: file,
      });

      if (!uploadRes.ok) {
        throw new Error(
          `Image upload failed with HTTP ${uploadRes.status}.`
        );
      }

      if (!signData.public_url) {
        throw new Error(
          "Upload completed, but no public image URL was returned."
        );
      }

      updateField("hero_image_url", signData.public_url);
      setHeroUploadMessage(
        "Image uploaded. Click Save Changes to publish it."
      );
    } catch (err) {
      console.error("Hero image upload failed:", err);
      setHeroUploadMessage(
        err?.message || "Image upload failed. Try again."
      );
    } finally {
      setHeroUploading(false);
      setHeroDragActive(false);
    }
  }

  async function saveChanges() {
    try {
      setSaving(true);
      setSaved(false);

      if (isFourthDownDemo) {
        window.setTimeout(() => {
          setSaving(false);
          setSaved(true);
        }, 350);
        return;
      }

      const {
        rating: _rating,
        review_count: _reviewCount,
        ...editableForm
      } = form;

      const res = await fetch(
        `${API_BASE}/api/mockups/client-portal/${token}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
         body: JSON.stringify({
  ...editableForm,

  offer:
    form.offer === "Custom..."
      ? form.custom_offer || ""
      : form.offer,

  cta_text:
    form.cta_text === "Custom..."
      ? form.custom_cta_text || ""
      : form.cta_text,

  after_claim_button_text: resolvedAfterClaimText(form),

  after_claim_button_link: resolvedAfterClaimLink(form),

  campaign_type: form.campaign_type || "offer",
           
        featured_item_name: form.featured_item_name || "",
        featured_item_description: form.featured_item_description || "",
        featured_action_text: form.featured_action_text || "View Menu",
        featured_action_link: normalizeExternalUrl(
          form.featured_action_link || form.menu_url
        ),

  event_name: form.event_name || "",
  event_date: form.event_date || "",
  event_time: form.event_time || "",
  event_description: form.event_description || "",
  event_action_text: resolvedEventActionText(form),
  event_action_link: eventDestinationLink(form, eventDestinationType),

  website_url: normalizeExternalUrl(form.website_url),
  menu_url: normalizeExternalUrl(form.menu_url),

  // Keep the existing Restaurant Page action field synchronized.
  cta_link: normalizeExternalUrl(resolvedAfterClaimLink(form)),
}),
        }
      );

      if (!res.ok) {
        throw new Error("Save failed");
      }

      setSaved(true);
    } catch (err) {
      console.error(err);
      alert("Could not save changes.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <main className="min-h-screen bg-[#09090B] text-white flex items-center justify-center">
        <p className="text-zinc-400">Loading TapToTable Dashboard...</p>
      </main>
    );
  }

  if (error) {
    return (
      <main className="min-h-screen bg-[#09090B] text-white flex items-center justify-center">
        <p className="text-red-400">{error}</p>
      </main>
    );
  }

  const publicPageUrl = form.slug
    ? `https://taptotable.co/r/${form.slug}`
    : "https://taptotable.co";

  const socialPromotionIdeas =
    form.campaign_type === "event"
      ? [
          {
            label: "Instagram Bio",
            text: `📅 See our latest event details & updates ↓\n${publicPageUrl}`,
          },
          {
            label: "Instagram / Facebook Post",
            text: `See what’s happening at ${form.restaurant_name || "our restaurant"}. Check event details, times, and the next step here: ${publicPageUrl}`,
          },
          {
            label: "Short Event Post",
            text: `Planning to join us? See our latest event details here: ${publicPageUrl}`,
          },
        ]
      : [
          {
            label: "Instagram Bio",
            text: `🍽️ See our latest promotion & restaurant updates ↓\n${publicPageUrl}`,
          },
          {
            label: "Instagram / Facebook Post",
            text: `Check out our latest promotion at ${form.restaurant_name || "our restaurant"}. Tap here to see what’s happening now: ${publicPageUrl}`,
          },
          {
            label: "Short Promotion Post",
            text: `See our current offer and restaurant updates here: ${publicPageUrl}`,
          },
        ];

  return (
    <main className="min-h-screen w-full max-w-full overflow-x-clip bg-[#09090B] px-3 py-4 text-white sm:p-6">
      <section className="mx-auto w-full max-w-3xl min-w-0">
        <p className="text-cyan-300 text-sm font-semibold uppercase tracking-wide">
          TapToTable Dashboard
        </p>

        <h1 className="mt-3 break-words text-2xl font-bold sm:text-3xl">
  {form.restaurant_name
    ? `${form.restaurant_name} Dashboard`
    : "Your TapToTable Dashboard"}
</h1>

        <p className="text-zinc-400 mt-3 mb-8">

Update your promotion, event, or featured item. Preview your changes instantly, then save when it looks right.

        </p>
<div className="mt-6 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4">
  <div className="text-sm font-semibold text-emerald-300">
    🟢 Your TapToTable page is live
  </div>
  <div className="text-sm text-zinc-300 mt-1">

Customers can view your current promotion, event, or featured item and take action from your public TapToTable Page.

  </div>
  <a
  href={`/r/${form.slug}?reset=1`}
  target="_blank"
  rel="noreferrer"
  className="inline-flex mt-4 rounded-full bg-emerald-500 px-4 py-2 text-sm font-semibold text-black hover:bg-emerald-400"
>
  View My Live Page
</a>
  <div className="mt-4 min-w-0 text-xs text-zinc-400">
  Public page:
  <span className="mt-2 block max-w-full break-all rounded bg-black/30 px-2 py-1 font-mono text-cyan-300">
    https://taptotable.co/r/{form.slug}
  </span>
</div>

<div className="mt-4 rounded-xl border border-amber-400/30 bg-amber-400/10 p-4">
  <div className="text-sm font-semibold text-amber-300">
    Reviewing Your Live Page?
  </div>
  <p className="mt-1 text-sm leading-relaxed text-zinc-300">
    Use <span className="font-semibold text-white">View My Live Page</span> to open the beginning of your current customer experience.
Review the promotion, event, or featured item, along with its buttons, links, and mobile layout before sharing your public link.

  </p>
</div>
  
</div>
        
        <section
          id="client-success-reminders"
          className="mt-6 scroll-mt-6 overflow-hidden rounded-2xl border border-emerald-400/25 bg-emerald-400/[0.045]"
        >
          <div className="border-b border-emerald-400/15 px-4 py-4 sm:px-5 sm:py-5">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div className="max-w-2xl">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-300">
                  TapToTable Success Reminders
                </div>
                <h2 className="mt-1 text-xl font-semibold text-white">
                  Keep Your TapToTable Page Fresh
                </h2>
                <p className="mt-2 text-sm leading-6 text-zinc-400">
                  TapToTable can remind you when it is a good time to refresh

your Promotion, Event, or Featured Item so customers have a new reason to visit.

                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  const nextEnabled = !form.success_reminders_enabled;
                  updateField("success_reminders_enabled", nextEnabled);

                  if (nextEnabled) {
                    updateField("success_reminder_email_enabled", true);
                  }
                }}
                className={`inline-flex w-full items-center justify-center rounded-full border px-4 py-2.5 text-sm font-semibold transition-colors sm:w-auto sm:min-w-[150px] ${
                  form.success_reminders_enabled
                    ? "border-emerald-400/40 bg-emerald-400/15 text-emerald-300"
                    : "border-white/15 bg-white/5 text-zinc-300 hover:bg-white/10"
                }`}
              >
                {form.success_reminders_enabled ? "✓ Reminders On" : "Reminders Off"}
              </button>
            </div>
          </div>

          <div className="p-5">
            {form.success_reminders_enabled ? (
              <div className="min-w-0 space-y-5">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-zinc-200">
                      How Often Should We Remind You?
                    </label>
                    <select
                      value={form.success_reminder_cadence || "biweekly"}
                      onChange={(e) => updateField("success_reminder_cadence", e.target.value)}
                      className="w-full rounded-xl border border-zinc-700 bg-zinc-900 px-4 py-3 text-white focus:border-emerald-400 focus:outline-none"
                    >
                      {SUCCESS_REMINDER_CADENCE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-zinc-200">
                      Preferred Reminder Day
                    </label>
                    <select
                      value={form.success_reminder_day || "Monday"}
                      onChange={(e) => updateField("success_reminder_day", e.target.value)}
                      className="w-full rounded-xl border border-zinc-700 bg-zinc-900 px-4 py-3 text-white focus:border-emerald-400 focus:outline-none"
                    >
                      {SUCCESS_REMINDER_DAY_OPTIONS.map((day) => (
                        <option key={day} value={day}>{day}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-white/10 bg-black/20 p-4">
                  <input
                    type="checkbox"
                    checked={form.success_reminder_email_enabled !== false}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      updateField("success_reminder_email_enabled", checked);

                      if (!checked) {
                        updateField("success_reminders_enabled", false);
                      } else if (!form.success_reminders_enabled) {
                        updateField("success_reminders_enabled", true);
                      }
                    }}
                    className="mt-1 h-4 w-4"
                  />
                  <span>
                    <span className="block text-sm font-semibold text-white">
                      Email me my Success Reminders
                    </span>
                    <span className="mt-1 block text-xs leading-5 text-zinc-400">
                      Success Reminders are delivered by email in V1. Turning this
                      off also turns Success Reminders off. Important account,
                      access, billing, and service emails are separate.
                    </span>
                  </span>
                </label>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Last Success Reminder</div>
                    <div className="mt-2 text-sm font-semibold text-white">
                      {form.success_reminder_last_sent_at ? formatReminderDate(form.success_reminder_last_sent_at) : "None sent yet"}
                    </div>
                  </div>
                  <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Next Reminder</div>
                    <div className="mt-2 text-sm font-semibold text-white">
                      {formatReminderDate(form.success_reminder_next_at)}
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-amber-400/20 bg-amber-400/[0.06] p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <div className="text-sm font-semibold text-amber-200">
                        Not ready to update your page yet?
                      </div>
                      <p className="mt-1 text-xs leading-5 text-zinc-400">
                        Keep your reminders on and move the next one ahead by 7 days.
                        Your Weekly / Every 2 Weeks / Monthly preference stays the same.
                        This saves automatically.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={remindMeLater}
                      disabled={snoozingReminder}
                      className="inline-flex min-h-[42px] shrink-0 items-center justify-center rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-2.5 text-sm font-semibold text-amber-200 transition-colors hover:bg-amber-400/15 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {snoozingReminder
                        ? "Snoozing..."
                        : "Remind Me in 7 Days"}
                    </button>
                  </div>

                  {reminderActionMessage ? (
                    <div className="mt-3 text-xs font-semibold text-emerald-300">
                      {reminderActionMessage}
                    </div>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                <div className="text-sm font-semibold text-white">Success Reminders are currently off.</div>
                <p className="mt-1 text-xs leading-5 text-zinc-400">
                  Turn them on whenever you want TapToTable to help remind you to refresh your Promotion or Event.
                </p>
              </div>
            )}

            <div className="mt-5 border-t border-white/10 pt-4 text-xs leading-5 text-zinc-500">
              Success Reminder emails will include a way to unsubscribe or manage reminder preferences.
              Unsubscribing from Success Reminders will not stop important TapToTable account or service emails.
            </div>

            <div className="mt-4">
              <p className="mb-3 text-xs leading-5 text-zinc-500">
                Use Save Reminder Preferences when you change On/Off, cadence,
                preferred day, or email delivery. Remind Me in 7 Days saves immediately.
              </p>
              <button
                type="button"
                onClick={saveChanges}
                disabled={saving}
                className="inline-flex min-h-[46px] items-center justify-center rounded-xl bg-emerald-400 px-5 py-3 text-sm font-bold text-black transition-colors hover:bg-emerald-300 disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save Reminder Preferences"}
              </button>
            </div>
          </div>
        </section>

        <div className="mt-8 mb-4 overflow-hidden rounded-2xl border border-white/10 bg-[#18181C]">
          <div className="flex items-stretch">
            <div className="w-1.5 flex-none bg-cyan-400" />
            <div className="flex-1 px-5 py-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-300">
                Dashboard Overview
              </p>
              <div className="mt-1 flex items-center gap-3">
                <h2 className="text-xl font-semibold text-white">
                  Performance
                </h2>
                <div className="h-px flex-1 bg-gradient-to-r from-white/15 to-transparent" />
              </div>
              <p className="mt-2 text-sm text-zinc-400">

              </p>
{form.campaign_type === "event"
  ? "Track visits and event actions for your current TapToTable Page."
  : form.campaign_type === "featured_item"
    ? "Track visits to your Featured Item page."
    : "Track visits and claims for your current TapToTable Page."}

            </div>
          </div>
        </div>
  <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">

  <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
    <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">
      Page Visits
    </div>
    <div className="mt-2 text-3xl font-bold text-white">
      {form.page_visits || 0}
    </div>
  </div>

  <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
    <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">

{form.campaign_type === "event"
  ? "Event Actions"
  : form.campaign_type === "featured_item"
    ? "Featured Item Actions"
    : "Offers Claimed"}

    </div>
    <div className="mt-2 text-3xl font-bold text-white">

{form.campaign_type === "featured_item"
  ? "—"
  : form.campaign_type === "event"
    ? form.event_actions || 0
    : form.offer_claims || 0}

    </div>
  </div>

  <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
    <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">

{form.campaign_type === "featured_item"
  ? "Featured Item Action Rate"
  : form.campaign_type === "event"
    ? "Event Action Rate"
    : "Claim Rate"}

    </div>
    <div className="mt-2 text-3xl font-bold text-white">

{form.campaign_type === "featured_item"
  ? "—"
  : (form.page_visits || 0) > 0
    ? `${Math.round(
        (((form.campaign_type === "event"
          ? form.event_actions
          : form.offer_claims) || 0) /
          (form.page_visits || 1)) *
          100
      )}%`
    : "—"}

    </div>
  </div>

</div>

        <div className="mt-5 rounded-2xl border border-white/10 bg-white/5 p-5">
          <div className="text-sm font-semibold text-white">
            Understanding Performance
          </div>

          <div className="mt-4 grid gap-4 md:grid-cols-3">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-cyan-300">
                Page Visits
              </div>
              <p className="mt-1 text-xs leading-relaxed text-zinc-400">
                How many times customers opened your TapToTable Page.
              </p>
            </div>

            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-cyan-300">

{form.campaign_type === "event"
  ? "Event Actions"
  : form.campaign_type === "featured_item"
    ? "Featured Item Actions"
    : "Offers Claimed"}

              </div>
              <p className="mt-1 text-xs leading-relaxed text-zinc-400">

{form.campaign_type === "event"
  ? "How many customers used the main event button."
  : form.campaign_type === "featured_item"
    ? "Featured Item button-click tracking is not available yet."
    : "How many customers completed the offer-claim step."}

              </p>
            </div>

            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-cyan-300">

{form.campaign_type === "event"
  ? "Event Action Rate"
  : form.campaign_type === "featured_item"
    ? "Featured Item Action Rate"
    : "Claim Rate"}

              </div>
              <p className="mt-1 text-xs leading-relaxed text-zinc-400">

{form.campaign_type === "event"
  ? "The percentage of page visits that became event actions."
  : form.campaign_type === "featured_item"
    ? "Featured Item action-rate tracking is not available yet."
    : "The percentage of page visits that became offer claims."}

              </p>
            </div>
          </div>
        </div>

        <div id="client-edit-top" className="mt-8 scroll-mt-6">
          <div className="mb-5 overflow-hidden rounded-2xl border border-cyan-400/20 bg-[#18181C]">
            <div className="flex items-stretch">
              <div className="w-1.5 flex-none bg-cyan-400" />
              <div className="flex-1 px-5 py-5">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-300">
                  How This Works
                </p>
                <h2 className="mt-1 text-2xl font-semibold text-white">

{form.campaign_type === "featured_item"
  ? "Feature your item. Preview your page. Save when it looks right."
  : form.campaign_type === "event"
    ? "Edit your event. Preview the customer experience. Save when it looks right."
    : "Edit before claim. Check after claim. Save when it looks right."}

                </h2>
                <p className="mt-2 text-sm leading-6 text-zinc-400">
                  Make changes in Page Settings. The Customer Preview updates
                  instantly. Nothing changes on your live page until you click
                  Save Changes.
                </p>

                <div className="mt-4 flex flex-wrap gap-2">
                  {["1. Edit", "2. Preview", "3. Save"].map((item) => (
                    <span
                      key={item}
                      className="rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1.5 text-xs font-semibold text-cyan-200"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div
            id="client-quick-edit"
            className="mb-5 scroll-mt-6 overflow-hidden rounded-2xl border border-cyan-400/25 bg-cyan-400/[0.05]"
          >
            <div className="border-b border-cyan-400/15 px-4 py-4 sm:px-5">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-300">
                Quick Edit
              </div>
              <h2 className="mt-1 break-words text-lg font-semibold text-white sm:text-xl">
                Jump Straight to What You Want to Change
              </h2>
              <p className="mt-2 text-sm leading-6 text-zinc-400">
                No need to scroll through the whole Dashboard. Pick a section
                below and TapToTable will take you there.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-2 p-4 sm:grid-cols-2 lg:grid-cols-4">
              {[
                ["1. Promotion / Event", "client-section-type"],
                ["2. Page Photo", "client-section-photo"],
                ["3. Before Claim", "client-section-content"],
                ["4. After Claim", "client-section-action"],
                ["5. Restaurant Info", "client-section-info"],
                ["Success Reminders", "client-success-reminders"],
                ["Help & Ideas", "client-help-resources"],
              ].map(([label, sectionId]) => (
                <button
                  key={sectionId}
                  type="button"
                  onClick={() => jumpToSection(sectionId)}
                  className="min-h-[48px] w-full min-w-0 whitespace-normal break-words rounded-xl border border-white/10 bg-black/20 px-3 py-3 text-center text-sm font-semibold text-zinc-200 transition-colors hover:border-cyan-400/35 hover:bg-cyan-400/10 hover:text-white"
                >
                  {label}
                </button>
              ))}

              <button
                type="button"
                onClick={() => {
                  if (window.innerWidth < 1024) {
                    setMobilePreviewOpen(true);
                  } else {
                    jumpToSection("client-customer-preview");
                  }
                }}
                className="min-h-[48px] w-full min-w-0 whitespace-normal break-words rounded-xl border border-cyan-400/30 bg-cyan-400/10 px-3 py-3 text-center text-sm font-semibold text-cyan-200 transition-colors hover:bg-cyan-400/15"
              >
                Preview
              </button>

              <button
                type="button"
                onClick={saveChanges}
                disabled={saving}
                className="min-h-[48px] w-full min-w-0 whitespace-normal break-words rounded-xl bg-cyan-400 px-3 py-3 text-center text-sm font-bold text-black transition-colors hover:bg-cyan-300 disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>

          <div className="grid w-full min-w-0 gap-6 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start">
            <div className="min-w-0 space-y-5">
              <section id="client-section-type" className="min-w-0 max-w-full scroll-mt-6 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.035]">
                <div className="border-b border-white/10 px-4 py-4 sm:px-5">
                  <div className="inline-flex items-center rounded-full border border-cyan-400/30 bg-cyan-400/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
                    1 · Choose What You’re Promoting
                  </div>

                  <h2 className="mt-3 text-xl font-semibold text-white">
                    What Are You Promoting?
                  </h2>
                  <p className="mt-1 text-sm leading-6 text-zinc-400">
                    Pick the type of page you want customers to see. You can
                    switch between these anytime.
                  </p>
                </div>

               

                <div className="grid gap-3 p-5 sm:grid-cols-3">


 
              {[
                {
                  value: "offer",
                  title: "Offer / Promotion",
                  description:
                    "Promote a special, reward, discount, or limited-time reason to visit.",
                },
                {
                  value: "event",
                  title: "Event",
                  description:
                    "Promote live music, a tasting, guest list, tickets, reservations, or another event.",
                },
                {
                  value: "featured_item",
                  title: "Featured Item",
                  description:
                    "Showcase a menu favorite without requiring a discount or claim code.",
                },
              ].map((option) => {

                    const active =
                      (form.campaign_type || "offer") === option.value;

                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => {
                          updateField("campaign_type", option.value);
                          setPreviewScreen("primary");
                        }}
                        className={`min-h-[132px] rounded-2xl border p-4 text-left transition-all ${
                          active
                            ? option.value === "event"
                              ? "border-violet-400/70 bg-violet-400/10 ring-1 ring-violet-400/30"
                              : "border-cyan-400/70 bg-cyan-400/10 ring-1 ring-cyan-400/30"
                            : "border-white/10 bg-black/20 hover:border-white/20 hover:bg-white/[0.04]"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="text-base font-semibold text-white">
                            {option.title}
                          </div>
                          <div
                            className={`flex h-6 w-6 items-center justify-center rounded-full border text-xs font-bold ${
                              active
                                ? "border-white/30 bg-white text-black"
                                : "border-white/15 text-zinc-500"
                            }`}
                          >
                            {active ? "✓" : ""}
                          </div>
                        </div>

                        <p className="mt-2 text-sm leading-5 text-zinc-400">
                          {option.description}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </section>

              <div id="client-section-photo" className="min-w-0 max-w-full scroll-mt-6 overflow-hidden rounded-2xl border border-cyan-400/25 bg-cyan-400/[0.04] shadow-[0_0_0_1px_rgba(34,211,238,0.03)]">
                <div className="border-b border-cyan-400/15 bg-cyan-400/10 px-5 py-4">
                  <div className="inline-flex items-center rounded-full border border-cyan-400/30 bg-cyan-400/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
                    2 · Choose Your Page Photo
                  </div>

                  <div className="mt-3 flex items-center gap-3">
                    <div className="h-8 w-1 rounded-full bg-cyan-400" />
                    <div>
                      <h2 className="text-xl font-semibold text-white">
                        Choose a Photo for Your Page
                      </h2>
                      <p className="mt-1 text-sm leading-6 text-zinc-400">
                        Pick the main photo customers will see on your TapToTable Page.
                        Upload one from your computer or phone, or drag and drop it below.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-5">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <p className="text-sm font-semibold text-white">
                        Featured Restaurant Photo
                      </p>
                      <p className="mt-1 text-xs leading-5 text-zinc-400">
                        JPG, PNG, WEBP, and GIF are supported up to 10 MB.
                      </p>
                    </div>

                    {heroUploading ? (
                      <div className="text-xs font-semibold text-cyan-300">
                        Uploading…
                      </div>
                    ) : null}
                  </div>

                  {form.hero_image_url ? (
                    <div className="mt-4 overflow-hidden rounded-xl border border-white/10 bg-black/30">
                      <img
                        src={form.hero_image_url}
                        alt={`${form.restaurant_name || "Restaurant"} featured`}
                        className="h-56 w-full object-cover"
                      />
                    </div>
                  ) : null}

                  <div
                    onDragEnter={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!heroUploading) setHeroDragActive(true);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!heroUploading) setHeroDragActive(true);
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setHeroDragActive(false);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setHeroDragActive(false);
                      const file = e.dataTransfer.files?.[0];
                      if (file) handleHeroImageFile(file);
                    }}
                    className={`mt-4 rounded-2xl border-2 border-dashed p-6 text-center transition-colors ${
                      heroDragActive
                        ? "border-cyan-400 bg-cyan-400/10"
                        : "border-white/15 bg-black/15"
                    }`}
                  >
                    <div className="text-sm font-semibold text-white">
                      Drag &amp; drop a restaurant image here
                    </div>
                    <div className="mt-1 text-xs text-zinc-500">
                      or choose a file from your computer or phone
                    </div>

                    <label
                      className={`mt-4 inline-flex cursor-pointer items-center justify-center rounded-xl px-5 py-3 text-sm font-bold transition-colors ${
                        heroUploading
                          ? "cursor-not-allowed bg-zinc-700 text-zinc-400"
                          : "bg-cyan-400 text-black hover:bg-cyan-300"
                      }`}
                    >
                      {heroUploading ? "Uploading Image…" : "Choose Image"}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        className="hidden"
                        disabled={heroUploading}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleHeroImageFile(file);
                          e.target.value = "";
                        }}
                      />
                    </label>
                  </div>

                  {heroUploadMessage ? (
                    <p
                      className={`mt-3 text-xs font-semibold ${
                        heroUploadMessage.startsWith("Image uploaded")
                          ? "text-emerald-300"
                          : "text-amber-300"
                      }`}
                    >
                      {heroUploadMessage}
                    </p>
                  ) : null}

                  <div className="mt-4">
                    <label className="mb-2 block text-sm text-zinc-300">
                      Featured Image URL
                    </label>
                    <input
                      value={form.hero_image_url || ""}
                      onChange={(e) =>
                        updateField("hero_image_url", e.target.value)
                      }
                      placeholder="https://..."
                      className="w-full rounded-xl border border-zinc-700 bg-zinc-900 px-4 py-3 text-white focus:border-cyan-400 focus:outline-none"
                    />
                    <p className="mt-2 text-xs leading-5 text-zinc-500">
                      You can still paste an image URL manually if you prefer.
                    </p>
                  </div>
                </div>
              </div>

              <section className="overflow-hidden rounded-2xl border border-cyan-400/25 bg-cyan-400/[0.04]">
                <button
                  type="button"
                  onClick={() => setAppearanceOpen((open) => !open)}
                  className="w-full px-5 py-4 text-left"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-300">
                        Page Appearance
                      </div>
                      <h2 className="mt-2 text-xl font-semibold text-white">
                        Choose Your Page Color
                      </h2>
                      <p className="mt-1 text-sm leading-6 text-zinc-400">
                        TapToTable recommends a color for your restaurant, but you can change it anytime.
                      </p>

                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/20 px-3 py-1.5 text-xs text-zinc-200">
                          <span
                            className="h-3.5 w-3.5 rounded-full border border-white/20"
                            style={{ backgroundColor: customerThemeColor(form) }}
                          />
                          Current: {colorNameFor(customerThemeColor(form))}
                        </span>
                        <span className="inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1.5 text-xs text-cyan-100">
                          <span
                            className="h-3.5 w-3.5 rounded-full border border-white/20"
                            style={{ backgroundColor: recommendedColor(form) }}
                          />
                          TapToTable Recommended: {colorNameFor(recommendedColor(form))}
                        </span>
                      </div>
                    </div>

                    <span className="flex-none pt-1 text-sm font-semibold text-cyan-300">
                      {appearanceOpen ? "Hide ▲" : "Edit Color ▼"}
                    </span>
                  </div>
                </button>

                {appearanceOpen ? (
                  <div className="border-t border-cyan-400/15 p-5">
                    <div className="grid gap-3 sm:grid-cols-4">
                      {CLIENT_COLOR_PRESETS.map(([label, color]) => {
                        const active =
                          customerThemeColor(form).toLowerCase() === color.toLowerCase();

                        return (
                          <button
                            key={color}
                            type="button"
                            onClick={() => updateField("brand_color", color)}
                            className={`flex items-center gap-3 rounded-xl border px-3 py-3 text-left transition-all ${
                              active
                                ? "border-white/40 bg-white/10 ring-1 ring-white/20"
                                : "border-zinc-700 bg-zinc-900 hover:border-zinc-500"
                            }`}
                          >
                            <span
                              className="h-7 w-7 flex-none rounded-lg border border-white/15"
                              style={{ backgroundColor: color }}
                            />
                            <span className="text-sm font-semibold text-white">
                              {label}
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    <p className="mt-4 text-xs leading-5 text-zinc-500">
                      Your TapToTable recommendation stays on record even if you choose a different color.
                    </p>
                  </div>
                ) : null}
              </section>

              {form.campaign_type === "featured_item" ? (
                <section
                  id="client-section-content"
                  className="scroll-mt-6 overflow-hidden rounded-2xl border border-cyan-400/25 bg-cyan-400/[0.04]"
                >
                  <div className="border-b border-cyan-400/15 px-5 py-4">
                    <div className="inline-flex rounded-full border border-cyan-400/30 bg-cyan-400/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
                      3 · Feature a Menu Item
                    </div>
                    <h2 className="mt-3 text-xl font-semibold text-white">
                      Featured Item Details
                    </h2>
                    <p className="mt-1 text-sm leading-6 text-zinc-400">
                      Showcase a menu favorite without offering a discount.
                      Your existing restaurant photo will appear on the page.
                    </p>
                  </div>

                  <div className="space-y-5 p-5">
                    <Field
                      label="Featured Item Name"
                      value={form.featured_item_name || ""}
                      onChange={(v) => updateField("featured_item_name", v)}
                    />

                    <div>
                      <label className="mb-2 block text-sm text-zinc-300">
                        Item Description
                      </label>
                      <textarea
                        value={form.featured_item_description || ""}
                        onChange={(e) =>
                          updateField("featured_item_description", e.target.value)
                        }
                        rows={4}
                        placeholder="Describe what makes this item special."
                        className="w-full rounded-xl border border-zinc-700 bg-zinc-900 px-4 py-3 text-white focus:border-cyan-400 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm text-zinc-300">
                        Customer Button
                      </label>
                      <select
                        value={form.featured_action_text || "View Menu"}
                        onChange={(e) =>
                          updateField("featured_action_text", e.target.value)
                        }
                        className="w-full rounded-xl border border-zinc-700 bg-zinc-900 px-4 py-3 text-white"
                      >
                        <option value="View Menu">View Menu</option>
                        <option value="Order Online">Order Online</option>
                        <option value="Learn More">Learn More</option>
                        <option value="Reserve Table">Reserve Table</option>
                      </select>
                    </div>

                    <Field
                      label="Button Destination URL"
                      value={form.featured_action_link || ""}
                      onChange={(v) => updateField("featured_action_link", v)}
                    />

                    <p className="text-xs leading-5 text-zinc-500">
                      Leave the URL blank to use your restaurant menu link.
                      Featured Items do not issue claim codes or require redemption.
                    </p>
                  </div>
                </section>
              ) : null}


              {form.campaign_type === "event" ? (
                <>
                  <section id="client-section-content" className="scroll-mt-6 overflow-hidden rounded-2xl border border-violet-400/25 bg-violet-400/[0.04]">
                    <div className="border-b border-violet-400/15 px-5 py-4">
                      <div className="inline-flex items-center rounded-full border border-violet-400/30 bg-violet-400/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-violet-300">
                        3 · Create Your Event Page
                      </div>
                      <h2 className="mt-3 text-xl font-semibold text-white">
                        Add the Event Details
                      </h2>
                      <p className="mt-1 text-sm leading-6 text-zinc-400">
                        Add your event message, date, time, details, and the main
                        action customers should take.
                      </p>

                      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2">
                        <button
                          type="button"
                          onClick={scrollToHelp}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-violet-300 hover:text-violet-200"
                        >
                          Need event guidance? View quick tips ↓
                        </button>
                        <button
                          type="button"
                          onClick={scrollToCopyIdeas}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-violet-300 hover:text-violet-200"
                        >
                          Need wording? See Copy &amp; Paste Ideas ↓
                        </button>
                      </div>
                    </div>

                    <div className="space-y-5 p-5">
                      <Field
                        label="Event Name"
                        value={form.event_name}
                        onChange={(v) => updateField("event_name", v)}
                      />

                      <div>
                        <div className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-zinc-500">
                          Quick Event Ideas
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {EVENT_NAME_IDEAS.map((idea) => (
                            <button
                              key={idea}
                              type="button"
                              onClick={() => updateField("event_name", idea)}
                              className="rounded-full border border-violet-400/20 bg-violet-400/5 px-3 py-2 text-xs font-semibold text-violet-100 transition-colors hover:bg-violet-400/10"
                            >
                              {idea}
                            </button>
                          ))}
                        </div>
                        <p className="mt-2 text-xs leading-5 text-zinc-500">
                          Click an idea to use it, then edit the Event Name however you like.
                        </p>
                      </div>

                      <div>
                        <label className="mb-2 block text-sm text-zinc-300">
                          Event Date
                        </label>
                        <input
                          type="date"
                          value={form.event_date || ""}
                          onChange={(e) =>
                            updateField("event_date", e.target.value)
                          }
                          onClick={(e) => e.currentTarget.showPicker?.()}
                          className="w-full cursor-pointer rounded-xl border border-zinc-700 bg-zinc-900 px-4 py-3 text-white focus:border-violet-400 focus:outline-none"
                        />
                        <p className="mt-2 text-xs text-zinc-500">
                          Click anywhere in the date box to open the calendar.
                        </p>
                      </div>

                      <div>
                        <label className="mb-2 block text-sm text-zinc-300">
                          Event Time
                        </label>

                        <div className="grid grid-cols-1 gap-4">
                          {[
                            [
                              "Starts",
                              "startHour",
                              "startMinute",
                              "startMeridiem",
                            ],
                            [
                              "Ends",
                              "endHour",
                              "endMinute",
                              "endMeridiem",
                            ],
                          ].map(
                            ([label, hourKey, minuteKey, meridiemKey]) => (
                              <div
                                key={label}
                                className="rounded-2xl border border-white/10 bg-black/20 p-4 sm:p-5"
                              >
                                <p className="mb-3 text-sm font-semibold uppercase tracking-[0.14em] text-violet-300">
                                  {label}
                                </p>

                                <div className="grid min-w-0 grid-cols-3 gap-2 sm:gap-3">
                                  <select
                                    value={
                                      parseEventTime(form.event_time)[hourKey]
                                    }
                                    onChange={(e) =>
                                      updateField(
                                        "event_time",
                                        updateEventTime(
                                          form.event_time,
                                          hourKey,
                                          e.target.value
                                        )
                                      )
                                    }
                                    className="min-h-[52px] min-w-0 w-full rounded-xl border border-zinc-700 bg-zinc-900 px-2 py-3 text-center text-base font-semibold text-white [color-scheme:dark] sm:px-3"
                                  >
                                    {EVENT_HOURS.map((hour) => (
                                      <option key={hour} value={hour}>
                                        {hour}
                                      </option>
                                    ))}
                                  </select>

                                  <select
                                    value={
                                      parseEventTime(form.event_time)[minuteKey]
                                    }
                                    onChange={(e) =>
                                      updateField(
                                        "event_time",
                                        updateEventTime(
                                          form.event_time,
                                          minuteKey,
                                          e.target.value
                                        )
                                      )
                                    }
                                    className="min-h-[52px] min-w-0 w-full rounded-xl border border-zinc-700 bg-zinc-900 px-2 py-3 text-center text-base font-semibold text-white [color-scheme:dark] sm:px-3"
                                  >
                                    {EVENT_MINUTES.map((minute) => (
                                      <option key={minute} value={minute}>
                                        :{minute}
                                      </option>
                                    ))}
                                  </select>

                                  <select
                                    value={
                                      parseEventTime(form.event_time)[
                                        meridiemKey
                                      ]
                                    }
                                    onChange={(e) =>
                                      updateField(
                                        "event_time",
                                        updateEventTime(
                                          form.event_time,
                                          meridiemKey,
                                          e.target.value
                                        )
                                      )
                                    }
                                    className="min-h-[52px] min-w-0 w-full rounded-xl border border-zinc-700 bg-zinc-900 px-2 py-3 text-center text-base font-semibold text-white [color-scheme:dark] sm:px-3"
                                  >
                                    {EVENT_MERIDIEMS.map((period) => (
                                      <option key={period} value={period}>
                                        {period}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            )
                          )}
                        </div>

                        <p className="mt-2 text-xs text-zinc-500">
                          {form.event_time ||
                            "Choose a start and end time."}
                        </p>
                      </div>

                      <div>
                        <Field
                          label="Your Event Message"
                          value={form.event_description}
                          onChange={(v) =>
                            updateField("event_description", v)
                          }
                          textarea
                        />
                        <p className="mt-2 text-xs leading-5 text-zinc-500">
                          Tell customers what the event is, when it is happening,
                          and why they should join.
                        </p>
                      </div>
                    </div>
                  </section>

                  <section id="client-section-action" className="scroll-mt-6 overflow-hidden rounded-2xl border border-violet-400/25 bg-violet-400/[0.04]">
                    <div className="border-b border-violet-400/15 px-5 py-4">
                      <div className="inline-flex items-center rounded-full border border-violet-400/30 bg-violet-400/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-violet-300">
                        4 · Choose the Main Event Button
                      </div>
                      <h2 className="mt-3 text-xl font-semibold text-white">
                        What Should Customers Do?
                      </h2>
                      <p className="mt-1 text-sm leading-6 text-zinc-400">
                        Choose the main button customers should tap and where it should take them.
                      </p>
                      <p className="mt-2 hidden text-xs font-semibold leading-5 text-violet-300 lg:block">
                        Preview tip: click <span className="text-white">After Button Tap</span> in the Customer Preview on the right to see the customer's next step.
                      </p>
                      <p className="mt-2 text-xs font-semibold leading-5 text-violet-300 lg:hidden">
                        Preview tip: tap <span className="text-white">Preview</span> below, then choose <span className="text-white">After Button Tap</span>.
                      </p>
                    </div>

                    <div className="space-y-5 p-5">
                      <div>
                        <label className="mb-2 block text-sm text-zinc-300">
                          Main Event Button
                        </label>
                        <p className="mb-2 text-xs leading-5 text-zinc-500">
                          This is the main button customers tap on your Event Page.
                        </p>
                        <select
                          value={
                            form.event_action_text ||
                            "View Event Details"
                          }
                          onChange={(e) => {
                            const value = e.target.value;
                            updateField("event_action_text", value);

                            if (value !== "Custom...") {
                              updateField(
                                "custom_event_action_text",
                                ""
                              );
                            }

                            if (value === "Reserve Table") {
                              setEventDestinationType("reservation");
                            } else if (["RSVP", "Get Tickets", "Join Guest List", "Save My Spot"].includes(value)) {
                              setEventDestinationType("tickets_rsvp");
                            } else if (["View Event Details", "See Event Details", "Plan My Visit"].includes(value)) {
                              setEventDestinationType((current) =>
                                ["website", "menu"].includes(current) ? current : "event_details"
                              );
                            }
                          }}
                          className="w-full rounded-xl border-2 border-violet-400/60 bg-zinc-900 px-4 py-3 text-white focus:border-violet-400 focus:outline-none"
                        >
                          {EVENT_ACTION_OPTIONS.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>

                        <button
                          type="button"
                          onClick={scrollToCopyIdeas}
                          className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-violet-300 hover:text-violet-200"
                        >
                          Need wording ideas? See Copy &amp; Paste Ideas below ↓
                        </button>

                        {form.event_action_text === "Custom..." && (
                          <div className="mt-3">
                            <Field
                              label="Your Custom Event Button"
                              value={
                                form.custom_event_action_text || ""
                              }
                              onChange={(v) =>
                                updateField(
                                  "custom_event_action_text",
                                  v
                                )
                              }
                            />
                          </div>
                        )}
                      </div>

                      <div className="rounded-2xl border border-violet-400/20 bg-black/20 p-4">
                        <label className="mb-2 block text-sm font-semibold text-zinc-200">
                          Where This Button Goes
                        </label>
                        <p className="mb-3 text-xs leading-5 text-zinc-500">
                          Pick a destination. TapToTable can use your saved website or menu automatically, or you can paste another link.
                        </p>

                        <select
                          value={eventDestinationType}
                          onChange={(e) => {
                            const nextType = e.target.value;
                            setEventDestinationType(nextType);
                            if (nextType === "website") {
                              updateField("event_action_link", normalizeExternalUrl(form.website_url));
                            } else if (nextType === "menu") {
                              updateField("event_action_link", normalizeExternalUrl(form.menu_url));
                            } else if (nextType === "none") {
                              updateField("event_action_link", "");
                            }
                          }}
                          className="w-full rounded-xl border-2 border-violet-400/60 bg-zinc-900 px-4 py-3 text-white focus:border-violet-400 focus:outline-none"
                        >
                          {EVENT_DESTINATION_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>

                        {eventDestinationType === "website" ? (
                          <div className="mt-3 rounded-xl border border-emerald-400/20 bg-emerald-400/10 px-4 py-3">
                            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-emerald-300">Uses Restaurant Website Automatically</div>
                            <p className="mt-1 break-all text-xs leading-5 text-zinc-300">
                              {form.website_url ? normalizeExternalUrl(form.website_url) : "Add a website in Restaurant Info first."}
                            </p>
                          </div>
                        ) : eventDestinationType === "menu" ? (
                          <div className="mt-3 rounded-xl border border-emerald-400/20 bg-emerald-400/10 px-4 py-3">
                            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-emerald-300">Uses Menu Link Automatically</div>
                            <p className="mt-1 break-all text-xs leading-5 text-zinc-300">
                              {form.menu_url ? normalizeExternalUrl(form.menu_url) : "Add a menu link in Restaurant Info first."}
                            </p>
                          </div>
                        ) : eventDestinationType === "none" ? (
                          <div className="mt-3 rounded-xl border border-amber-400/20 bg-amber-400/10 px-4 py-3 text-xs leading-5 text-amber-200">
                            No destination is set yet. The button can still be shown while you finish the event setup.
                          </div>
                        ) : (
                          <div className="mt-3">
                            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-zinc-500">Destination Link</label>
                            <input
                              value={form.event_action_link || ""}
                              onChange={(e) => updateField("event_action_link", e.target.value)}
                              onBlur={(e) => updateField("event_action_link", normalizeExternalUrl(e.target.value))}
                              placeholder={eventDestinationPlaceholder(eventDestinationType)}
                              className="w-full rounded-xl border border-zinc-700 bg-zinc-900 px-4 py-3 text-white focus:border-violet-400 focus:outline-none"
                            />
                            <p className="mt-2 text-xs leading-5 text-zinc-500">
                              You can enter <span className="text-zinc-300">www.site.com</span> or a full <span className="text-zinc-300">https://</span> address. TapToTable adds https:// automatically when needed.
                            </p>
                          </div>
                        )}

                        <p className="mt-3 text-xs leading-5 text-zinc-500">
                          {EVENT_DESTINATION_OPTIONS.find((option) => option.value === eventDestinationType)?.description}
                        </p>
                      </div>

                      <div className="rounded-xl border border-white/10 bg-black/20 px-4 py-4">
                        <div className="text-xs font-semibold uppercase tracking-[0.14em] text-zinc-400">
                          Automatic Customer Buttons
                        </div>
                        <div className="mt-3 grid min-w-0 grid-cols-3 gap-2">
                          {["Website", "Call", "Directions"].map(
                            (label) => {
                              const ready =
                                automaticUtilityButtons(form).some(
                                  (button) =>
                                    button.label === label
                                );

                              return (
                                <div
                                  key={label}
                                  className={`rounded-xl border px-2 py-2.5 text-center text-xs font-semibold ${
                                    ready
                                      ? "border-white/10 bg-white/5 text-zinc-200"
                                      : "border-white/5 bg-black/20 text-zinc-600"
                                  }`}
                                >
                                  {label}
                                </div>
                              );
                            }
                          )}
                        </div>
                        <p className="mt-2 text-xs leading-5 text-zinc-500">
                          These are created automatically from Restaurant Info.
                        </p>
                      </div>
                    </div>
                  </section>
                </>

              ) : form.campaign_type === "offer" ? (

                <>
<div id="client-section-content" className="min-w-0 max-w-full scroll-mt-6 rounded-2xl border border-cyan-400/25 bg-cyan-400/[0.04] p-4 space-y-5 shadow-[0_0_0_1px_rgba(34,211,238,0.03)] sm:p-6">
                <div className="overflow-hidden rounded-2xl border border-cyan-400/30 bg-cyan-400/10">
                  <div className="border-b border-cyan-400/15 px-4 py-3">
                    <div className="inline-flex items-center rounded-full bg-cyan-400/15 border border-cyan-400/30 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
                      3 · Before Claim
                    </div>
                  </div>

                  <div className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-1 rounded-full bg-cyan-400" />
                      <h2 className="text-xl font-semibold text-white">
                        Edit What Customers See Before Claiming
                      </h2>
                      <div className="h-px flex-1 bg-gradient-to-r from-white/15 to-transparent" />
                    </div>
                    <p className="mt-2 text-sm text-zinc-400">
                      These are the customer-facing details customers see before
                      they claim the offer.
                    </p>

                    <button
                      type="button"
                      onClick={scrollToHelp}
                      className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-cyan-300 hover:text-cyan-200"
                    >
                      Not sure what to promote? View quick tips ↓
                    </button>
                  </div>
                </div>

                <Field
                  label="→ Restaurant Name"
                  value={form.restaurant_name}
                  onChange={(v) => updateField("restaurant_name", v)}
                />

                <div>
                  <label className="mb-2 block text-sm text-zinc-300">
                    Promotion Message Idea
                  </label>
                  <p className="mb-2 text-xs leading-5 text-zinc-500">
                    Pick a ready-made idea to get started. You can change any
                    wording in the text box below.
                  </p>

                  <select
                    value={
                      PROMOTION_MESSAGE_IDEAS.includes(form.description)
                        ? form.description
                        : "Custom..."
                    }
                    onChange={(e) => {
                      const nextValue = e.target.value;

                      if (nextValue === "Custom...") {
                        if (PROMOTION_MESSAGE_IDEAS.includes(form.description)) {
                          updateField("description", "");
                        }
                        return;
                      }

                      updateField("description", nextValue);
                    }}
                    className="w-full rounded-xl border border-zinc-700 bg-zinc-900 px-4 py-3 text-white focus:border-cyan-400 focus:outline-none"
                    data-testid="client-promotion-message-idea"
                  >
                    <option value="Custom...">Custom Message...</option>
                    {PROMOTION_MESSAGE_IDEAS.map((idea, index) => (
                      <option key={idea} value={idea}>
                        {index + 1}. {idea}
                      </option>
                    ))}
                  </select>

                  <div className="mt-4">
                    <Field
                      label="→ Promotion Message"
                      value={form.description}
                      onChange={(v) => updateField("description", v)}
                      textarea
                    />
                  </div>

                  <p className="mt-2 text-xs leading-5 text-zinc-500">
                    Use one of the ideas above as-is, edit it to fit your
                    restaurant, or type your own message. This appears under
                    “Before Claim” in the Customer Preview.
                  </p>
                </div>

                <div>
                  <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-cyan-200">
                    <span className="font-bold text-cyan-300" aria-hidden>→</span>
                    Offer Customers Claim
                  </label>
                  <p className="mb-2 text-xs leading-5 text-zinc-500">
                    The main reason customers will tap and claim.
                  </p>

                  <select
                    value={
                      form.offer === "Custom..."
                        ? "Custom..."
                        : offerChoicesFor(form).includes(form.offer)
                          ? form.offer
                          : "Custom..."
                    }
                    onChange={(e) => {
                      const nextValue = e.target.value;

                      if (nextValue === "Custom...") {
                        updateField("custom_offer", form.offer || "");
                        updateField("offer", "Custom...");
                      } else {
                        updateField("custom_offer", "");
                        updateField("offer", nextValue);
                      }
                    }}
                    className="w-full rounded-xl bg-zinc-900 border border-zinc-700 px-4 py-3 text-white focus:outline-none focus:border-cyan-400"
                  >
                    {offerChoicesFor(form).map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                    <option value="Custom...">Custom Offer...</option>
                  </select>

                  {form.offer === "Custom..." && (
                    <div className="mt-3">
                      <Field
                        label="Your Custom Offer"
                        value={form.custom_offer || ""}
                        onChange={(v) => updateField("custom_offer", v)}
                      />
                    </div>
                  )}
                </div>

                <div>
                  <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-cyan-200">
                    <span className="font-bold text-cyan-300" aria-hidden>→</span>
                    Claim Button
                  </label>
                  <p className="mb-2 text-xs leading-5 text-zinc-500">
                    This is the button customers tap to claim the offer and receive
                    their code. Pick a ready-made option or choose Custom.
                  </p>

                  <select
                    value={
                      form.cta_text === "Custom..."
                        ? "Custom..."
                        : buttonChoicesFor(form).includes(form.cta_text)
                          ? form.cta_text
                          : "Custom..."
                    }
                    onChange={(e) => {
                      const nextValue = e.target.value;

                      if (nextValue === "Custom...") {
                        updateField("custom_cta_text", form.cta_text || "");
                        updateField("cta_text", "Custom...");
                      } else {
                        updateField("custom_cta_text", "");
                        updateField("cta_text", nextValue);
                      }
                    }}
                    className="w-full rounded-xl bg-zinc-900 border-2 border-cyan-400/60 px-4 py-3 text-white focus:outline-none focus:border-cyan-400"
                  >
                    {buttonChoicesFor(form).map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                    <option value="Custom...">Custom...</option>
                  </select>

                  <button
                    type="button"
                    onClick={scrollToCopyIdeas}
                    className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-cyan-300 hover:text-cyan-200"
                  >
                    Need wording ideas? See Copy &amp; Paste Ideas below ↓
                  </button>

                  {form.cta_text === "Custom..." && (
                    <div className="mt-3">
                      <Field
                        label="Your Custom Main Button"
                        value={form.custom_cta_text || ""}
                        onChange={(v) =>
                          updateField("custom_cta_text", v)
                        }
                      />
                    </div>
                  )}
                </div>

              </div>

              <div id="client-section-action" className="min-w-0 max-w-full scroll-mt-6 rounded-2xl border border-green-400/25 bg-green-400/[0.04] p-4 space-y-5 shadow-[0_0_0_1px_rgba(74,222,128,0.03)] sm:p-6">
                <div className="overflow-hidden rounded-2xl border border-green-400/30 bg-green-400/10">
                  <div className="border-b border-green-400/15 px-4 py-3">
                    <div className="inline-flex items-center rounded-full bg-green-400/15 border border-green-400/30 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-green-300">
                      4 · After Claim
                    </div>
                  </div>

                  <div className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-1 rounded-full bg-green-400" />
                      <h2 className="text-xl font-semibold text-white">
                        Edit What Customers See After Claiming
                      </h2>
                      <div className="h-px flex-1 bg-gradient-to-r from-white/15 to-transparent" />
                    </div>
                    <p className="mt-2 text-sm text-zinc-400">
                      This controls the main action customers see after receiving
                      their claim code. TapToTable recommends an action that matches
                      the promotion whenever possible.
                    </p>
                  </div>
                </div>

                <div className="rounded-xl border border-cyan-400/25 bg-cyan-400/10 px-4 py-3">
                  <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-cyan-300">
                    Recommended for this promotion
                  </div>
                  <div className="mt-1 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <div className="text-sm font-semibold text-white">
                        {recommendedAfterClaimAction(form)}
                      </div>
                      <p className="mt-1 text-xs leading-5 text-zinc-400">
                        {isVisitOrientedPromotion(form)
                          ? "This promotion is written to drive an in-person visit, so the After Claim button should help customers plan that visit."
                          : isOrderingOrTakeoutPromotion(form)
                            ? "Order Online is recommended only when you have a dedicated ordering link."
                            : "TapToTable chooses the most useful available restaurant destination."}
                      </p>
                    </div>

                    {resolvedAfterClaimText(form) !== recommendedAfterClaimAction(form) ? (
                      <button
                        type="button"
                        onClick={() => {
                          const nextAction = recommendedAfterClaimAction(form);
                          setForm((prev) => ({
                            ...prev,
                            after_claim_button_text: nextAction,
                            after_claim_button_link:
                              ["View Menu", "Get Directions", "Visit Website"].includes(nextAction)
                                ? ""
                                : prev.after_claim_button_link,
                          }));
                        }}
                        className="shrink-0 rounded-lg border border-cyan-400/30 bg-cyan-400/15 px-3 py-2 text-xs font-bold text-cyan-200 hover:bg-cyan-400/20"
                      >
                        Use Recommended
                      </button>
                    ) : null}
                  </div>
                </div>

                <div>
                  <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-green-300">
                    <span className="font-bold text-cyan-300" aria-hidden>→</span>
                    Main Button After Claim
                  </label>
                  <p className="mb-2 text-xs leading-5 text-zinc-500">
                    Choose the main next step for customers after they claim
                    your offer.
                  </p>

                  <select
                    value={form.after_claim_button_text}
                    onChange={(e) => {
                      const nextValue = e.target.value;

                      if (nextValue === "Custom...") {
                        updateField(
                          "custom_after_claim_button_text",
                          form.custom_after_claim_button_text || ""
                        );
                      }

                      updateField("after_claim_button_text", nextValue);

                      if (
                        ["View Menu", "Get Directions", "Visit Website"].includes(
                          nextValue
                        )
                      ) {
                        updateField("after_claim_button_link", "");
                      }
                    }}
                    className="w-full rounded-xl bg-zinc-900 border-2 border-green-400/60 px-4 py-3 text-white focus:outline-none focus:border-green-400"
                  >
                    {AFTER_CLAIM_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>

                  {form.after_claim_button_text === "Custom..." && (
                    <div className="mt-3">
                      <Field
                        label="Your Custom After-Claim Button"
                        value={form.custom_after_claim_button_text || ""}
                        onChange={(v) =>
                          updateField(
                            "custom_after_claim_button_text",
                            v
                          )
                        }
                      />
                    </div>
                  )}
                </div>

                {afterClaimNeedsLink(
                  form.after_claim_button_text
                ) ? (
                  <div>
                    <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-green-300">
                      <span className="h-2.5 w-2.5 rounded-full bg-green-400" />
                      Where This Button Goes
                    </label>
                    <p className="mb-2 text-xs leading-5 text-zinc-500">
                      Paste the exact page this button should open.
                    </p>
                    <input
                      value={form.after_claim_button_link || ""}
                      onChange={(e) =>
                        updateField("after_claim_button_link", e.target.value)
                      }
                      placeholder="https://..."
                      className="w-full rounded-xl bg-zinc-900 border-2 border-green-400/60 px-4 py-3 text-white focus:outline-none focus:border-green-400"
                    />
                  </div>
                ) : (
                  <div className="rounded-xl border border-green-400/25 bg-green-400/10 px-4 py-3">
                    <div className="text-xs font-semibold uppercase tracking-[0.14em] text-green-300">
                      Destination handled automatically
                    </div>
                    <p className="mt-1 text-xs leading-5 text-zinc-300">
                      {automaticDestinationMessage(form)}
                    </p>
                  </div>
                )}

                {afterClaimActionWarning(form) ? (
                  <div className="rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3">
                    <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-amber-300">
                      Check this action
                    </div>
                    <p className="mt-1 text-xs leading-5 text-amber-100/90">
                      {afterClaimActionWarning(form)}
                    </p>
                  </div>
                ) : null}

                <div className="rounded-xl border border-white/10 bg-black/20 px-4 py-3">
                  <div className="text-xs font-semibold uppercase tracking-[0.14em] text-zinc-400">
                    After Claim Will Show
                  </div>
                  <div className="mt-1 text-sm font-semibold text-white">
                    {resolvedAfterClaimText(form)}
                  </div>
                  <div className="mt-1 break-words text-xs text-zinc-500">
                    {resolvedAfterClaimLink(form) || "No destination link is available yet."}
                  </div>
                </div>

                <div className="rounded-xl border border-white/10 bg-black/20 px-4 py-3">
                  <div className="text-xs font-semibold uppercase tracking-[0.14em] text-zinc-400">
                    Automatic Customer Buttons
                  </div>
                  <p className="mt-1 text-xs leading-5 text-zinc-300">
                    Website, Call, and Directions are created automatically from your Restaurant Info when those details are available.
                  </p>
                </div>

                <div>
                  <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-cyan-200">
                    <span className="font-bold text-cyan-300" aria-hidden>→</span>
                    Offer Terms
                  </label>
                  <p className="mb-2 text-xs leading-5 text-zinc-500">
                    Keep redemption rules short and easy for customers and
                    staff to understand.
                  </p>

                  <select
                    value={
                      OFFER_TERMS_OPTIONS.includes(form.offer_terms)
                        ? form.offer_terms
                        : "Custom..."
                    }
                    onChange={(e) => {
                      if (e.target.value === "Custom...") {
                        updateField("offer_terms", "");
                      } else {
                        updateField("offer_terms", e.target.value);
                      }
                    }}
                    className="w-full rounded-xl bg-zinc-900 border border-zinc-700 px-4 py-3 text-white focus:outline-none focus:border-cyan-400"
                  >
                    {OFFER_TERMS_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>

                  <textarea
                    value={form.offer_terms || ""}
                    onChange={(e) =>
                      updateField("offer_terms", e.target.value)
                    }
                    rows={3}
                    placeholder="Enter offer terms..."
                    className="mt-3 w-full rounded-xl bg-zinc-900 border border-zinc-700 px-4 py-3 text-white focus:outline-none focus:border-cyan-400"
                  />
                </div>
              </div>

</>
) : null}



                          </div>

            {/* Keep desktop preview pinned while the left editor scrolls. overflow-x-clip on <main> is required for sticky to work. */}
            <aside id="client-customer-preview" className="hidden scroll-mt-6 lg:sticky lg:top-6 lg:block lg:self-start lg:max-h-[calc(100vh-3rem)] lg:overflow-y-auto lg:overscroll-contain lg:pr-1">
              <div className="mb-3">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-300">
                  Customer Preview
                </p>
                <p className="mt-1 text-sm text-zinc-400">
                  Your preview updates instantly while you edit.
                </p>

<p className="mt-2 text-xs leading-5 text-zinc-500">
  {form.campaign_type === "featured_item"
    ? "Preview your featured item, its description, and the button customers will use."
    : form.campaign_type === "event"
      ? "Preview your event page and what customers see after tapping the event button."
      : "Use the tabs below to check what customers see before claiming and after receiving a claim code."}
</p>

              </div>


            <div className={`mb-3 grid gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-1.5 ${form.campaign_type === "featured_item" ? "grid-cols-1" : "grid-cols-2"}`}>


{(form.campaign_type === "featured_item"
  ? [["primary", "Featured Item Page"]]
  : [
      [
        "primary",
        form.campaign_type === "event" ? "Event Page" : "Before Claim",
      ],
      [
        "secondary",
        form.campaign_type === "event" ? "After Button Tap" : "After Claim",
      ],
    ]
).map(([value, label]) => (

                  <button
                    key={value}
                    type="button"
                    onClick={() => setPreviewScreen(value)}
                    className={`rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${
                      previewScreen === value
                        ? "bg-cyan-400 text-black"
                        : "text-zinc-400 hover:bg-white/5 hover:text-white"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <PortalPreview
                form={form}
                compact
                screen={previewScreen}
              />
            </aside>
          </div>

          <div className="sticky bottom-3 z-30 mt-6 w-full min-w-0 max-w-full lg:hidden">
            <div className="grid w-full min-w-0 grid-cols-3 gap-2 rounded-2xl border border-white/10 bg-[#111114]/95 p-2 shadow-2xl backdrop-blur">
              <button
                type="button"
                onClick={jumpToQuickEdit}
                className="min-h-[46px] rounded-xl border border-white/10 bg-white/5 px-2 py-2 text-xs font-bold text-white"
              >
                Sections
              </button>
              <button
                type="button"
                onClick={() => setMobilePreviewOpen(true)}
                className="min-h-[46px] rounded-xl border border-cyan-400/30 bg-cyan-400/10 px-2 py-2 text-xs font-bold text-cyan-200"
              >
                Preview
              </button>
              <button
                type="button"
                onClick={saveChanges}
                disabled={saving}
                className="min-h-[46px] rounded-xl bg-cyan-400 px-2 py-2 text-xs font-bold text-black disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>

          <div id="client-section-info" className="mt-8 scroll-mt-6">
<div className="rounded-2xl border border-white/10 bg-white/5 p-6 space-y-5">
  <button
    type="button"
    onClick={() => setDetailsOpen(!detailsOpen)}
    className="w-full flex items-center justify-between gap-4 text-left"
  >
    <div className="flex flex-1 items-center gap-3">
      <div className="h-8 w-1 rounded-full bg-cyan-400" />
      <div>
        <div className="inline-flex items-center rounded-full border border-cyan-400/30 bg-cyan-400/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
          5 · Restaurant Info
        </div>
        <h2 className="mt-1 text-xl font-semibold text-white">
          Keep Your Restaurant Info Current
        </h2>
        <p className="text-sm text-zinc-400 mt-1">
          Phone, address, website, menu, and hours.
        </p>
      </div>
    </div>

    <span className="flex-none text-sm font-semibold text-cyan-300">
      {detailsOpen ? "Hide ▲" : "Edit ▼"}
    </span>
  </button>

  {detailsOpen && (
    <div className="space-y-5 border-t border-white/10 pt-5">
      <Field
        label="Phone"
        value={form.phone}
        onChange={(v) => updateField("phone", v)}
      />

      <Field
        label="Address"
        value={form.address}
        onChange={(v) => updateField("address", v)}
      />

      <Field
        label="Website URL"
        value={form.website_url}
        onChange={(v) => updateField("website_url", v)}
      />

      <Field
        label="Menu URL"
        value={form.menu_url}
        onChange={(v) => updateField("menu_url", v)}
      />

      <Field
        label="Hours"
        value={form.hours}
        onChange={(v) => updateField("hours", v)}
        textarea
      />
    </div>
  )}
</div>
          </div>

          <div className="mt-6 rounded-2xl border border-cyan-400/20 bg-cyan-400/[0.04] p-5">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-300">
              Edit Another Section
            </div>
            <p className="mt-2 text-sm text-zinc-400">
              Jump back to any part of your Dashboard without scrolling.
            </p>

            <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {[
                ["1. Promotion / Event", "client-section-type"],
                ["2. Page Photo", "client-section-photo"],
                ["3. Before Claim", "client-section-content"],
                ["4. After Claim", "client-section-action"],
                ["5. Restaurant Info", "client-section-info"],
                ["Success Reminders", "client-success-reminders"],
                ["Help & Ideas", "client-help-resources"],
              ].map(([label, sectionId]) => (
                <button
                  key={sectionId}
                  type="button"
                  onClick={() => jumpToSection(sectionId)}
                  className="min-h-[46px] rounded-xl border border-white/10 bg-black/20 px-3 py-2.5 text-sm font-semibold text-zinc-200 transition-colors hover:border-cyan-400/35 hover:bg-cyan-400/10"
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="mt-3">
              <button
                type="button"
                onClick={jumpToQuickEdit}
                className="min-h-[46px] w-full rounded-xl border border-cyan-400/30 bg-cyan-400/10 px-3 py-2.5 text-sm font-semibold text-cyan-200"
              >
                ↑ Back to Quick Edit
              </button>
            </div>
          </div>

          <div className="mt-6">
<div className="overflow-hidden rounded-2xl border border-white/10 bg-white/5">
  <div className="h-1 w-full bg-cyan-400" />
  <div className="p-5">
    <div>
      <div className="inline-flex items-center rounded-full border border-cyan-400/30 bg-cyan-400/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
        Final Step · Save &amp; Review
      </div>
      <h2 className="mt-1 text-xl font-semibold text-white">
        Save & Review Your Page
      </h2>
      <p className="mt-1 text-sm text-zinc-400">
        The preview updates immediately. Click Save Changes when
        everything looks right.
      </p>
    </div>

    <div className="mt-4 flex flex-col gap-3 sm:flex-row">
      <button
        onClick={saveChanges}
        disabled={saving}
        className="inline-flex min-h-[48px] flex-1 items-center justify-center rounded-xl bg-cyan-400 px-5 py-3 font-semibold text-black transition-colors hover:bg-cyan-300 disabled:opacity-50"
      >
        {saving ? "Saving..." : "Save Changes"}
      </button>

      <a
        href={`/r/${form.slug}?reset=1`}
        target="_blank"
        rel="noreferrer"
        className="inline-flex min-h-[48px] flex-1 items-center justify-center gap-2 rounded-xl border border-cyan-400/40 bg-transparent px-5 py-3 font-semibold text-cyan-200 transition-colors hover:bg-cyan-400/10"
      >
        <span aria-hidden>👁</span>
        <span>View Live Page</span>
      </a>
    </div>
  </div>
</div>
          </div>

          <div className="mt-4">
{saved && (
  <div className="rounded-xl border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm text-green-300">
    ✓ Your TapToTable Page has been updated successfully.
  </div>
)}
          </div>
        </div>

        {mobilePreviewOpen && (
          <div className="fixed inset-0 z-50 bg-black/80 p-4 lg:hidden">
            <div className="mx-auto flex h-full w-full max-w-md min-w-0 flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#09090B]">
              <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-300">
                    What Customers See
                  </p>
                  <p className="mt-1 text-sm text-zinc-400">
                    Your changes appear here instantly.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setMobilePreviewOpen(false)}
                  className="rounded-full border border-white/10 bg-white/5 px-3 py-2 text-sm font-semibold text-white"
                >
                  Close
                </button>
              </div>

              <div className="border-b border-white/10 px-4 py-3">

<div className={`grid gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-1.5 ${form.campaign_type === "featured_item" ? "grid-cols-1" : "grid-cols-2"}`}>


{(form.campaign_type === "featured_item"
  ? [["primary", "Featured Item Page"]]
  : [
      [
        "primary",
        form.campaign_type === "event" ? "Event Page" : "Before Claim",
      ],
      [
        "secondary",
        form.campaign_type === "event" ? "After Button Tap" : "After Claim",
      ],
    ]
).map(([value, label]) => (

                    <button
                      key={value}
                      type="button"
                      onClick={() => setPreviewScreen(value)}
                      className={`rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${
                        previewScreen === value
                          ? "bg-cyan-400 text-black"
                          : "text-zinc-400 hover:bg-white/5 hover:text-white"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4">
                <PortalPreview
                  form={form}
                  compact
                  screen={previewScreen}
                />
              </div>
            </div>
          </div>
        )}

  <div
    id="client-promote-page"
    className="mt-6 overflow-hidden rounded-2xl border border-cyan-400/25 bg-cyan-400/[0.05]"
  >
    <div className="border-b border-cyan-400/15 px-4 py-4 sm:px-5">
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-300">
        Next Step · Share Your TapToTable Page
      </div>
      <h3 className="mt-2 text-lg font-semibold text-white">
        Your Page Is Ready — Now Put It in Front of Customers
      </h3>
      <p className="mt-2 text-sm leading-relaxed text-zinc-400">
        Your page is built and ready to work for you. Make a post, add the
        TapToTable link to your bio or profile, and give customers a reason to tap.
        Use the ready-made copy below so you do not have to write it from scratch.
      </p>
    </div>

    <div className="p-5">
      <div className="rounded-xl border border-white/10 bg-black/25 p-4">
        <div className="text-xs font-semibold uppercase tracking-[0.14em] text-zinc-400">
          Your TapToTable Link
        </div>
        <div className="mt-2 break-all rounded-lg border border-cyan-400/20 bg-cyan-400/5 px-3 py-3 font-mono text-sm text-cyan-200">
          {publicPageUrl}
        </div>
        <button
          type="button"
          onClick={() => copyResourceText(publicPageUrl, "public-page-link")}
          className="mt-3 rounded-lg bg-cyan-400 px-4 py-2.5 text-xs font-bold text-black hover:bg-cyan-300"
        >
          {copiedResourceKey === "public-page-link"
            ? "✓ Link Copied"
            : "Copy My TapToTable Link"}
        </button>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-3">
        {socialPromotionIdeas.map((idea, index) => {
          const key = `social-promotion-${index}`;

          return (
            <div
              key={key}
              className="rounded-xl border border-white/10 bg-black/25 p-4"
            >
              <div className="text-xs font-semibold uppercase tracking-[0.12em] text-cyan-300">
                {idea.label}
              </div>
              <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-zinc-300">
                {idea.text}
              </p>
              <button
                type="button"
                onClick={() => copyResourceText(idea.text, key)}
                className="mt-3 rounded-lg border border-cyan-400/30 bg-cyan-400/10 px-3 py-2 text-xs font-semibold text-cyan-200 hover:bg-cyan-400/15"
              >
                {copiedResourceKey === key ? "✓ Copied" : "Copy This Post"}
              </button>
            </div>
          );
        })}
      </div>

      <div className="mt-4 rounded-xl border border-amber-400/25 bg-amber-400/10 p-4">
        <div className="text-sm font-semibold text-amber-300">
          Do This Next
        </div>
        <p className="mt-2 text-sm leading-relaxed text-zinc-300">
          Pick one ready-made post above, copy it, publish it to Instagram or
          Facebook, and keep your TapToTable link easy to find in your bio or
          profile. Update the post whenever you change your promotion or event.
        </p>
      </div>
    </div>
  </div>

<div
  id="client-help-resources"
  className="mt-10 scroll-mt-6 overflow-hidden rounded-2xl border border-cyan-400/20 bg-cyan-400/5"
>

  <div className="border-b border-cyan-400/15 bg-cyan-400/5 px-6 py-5">
    <div className="flex items-center gap-3">
      <div className="h-9 w-1 rounded-full bg-cyan-400" />
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-300">
          Help &amp; Resources
        </p>

        <h2 className="mt-1 text-xl font-semibold text-white">
          Using Your TapToTable Dashboard
        </h2>
      </div>
    </div>

    <p className="mt-3 text-sm leading-relaxed text-zinc-400">
      Use this dashboard to update what customers see, create promotions or
      events, review customer activity, and keep your restaurant information current.
      Use Customer Preview to check Before Claim and After Claim for promotions,
      or Event Page and After Button Tap for events, before saving.
    </p>
  </div>

  <div className="p-6">

    
<div className="mb-6 rounded-xl border border-cyan-400/40 bg-cyan-400/10 p-5">
  <h3 className="text-lg font-semibold text-white">
    Watch Your Dashboard Training Video
  </h3>

  <p className="mt-2 text-sm leading-relaxed text-zinc-300">
    New to TapToTable? Watch our complete walkthrough to learn
    how to create promotions, manage events, customize your page,
    and share your TapToTable link with customers.
  </p>

  <a
    href="https://taptotable.co/training"
    target="_blank"
    rel="noopener noreferrer"
    className="mt-4 inline-flex items-center justify-center rounded-lg bg-cyan-400 px-5 py-3 text-sm font-semibold text-black transition hover:bg-cyan-300"
  >
    Watch Dashboard Tutorial
  </a>

  <p className="mt-3 text-xs text-zinc-400">
    Complete video walkthrough · Approximately 8 minutes
  </p>
</div>

  <div className="mt-6 grid gap-4 md:grid-cols-2">
    {[
      [
        "1. Choose What You’re Promoting",
        "Choose Offer / Promotion or Event. This changes the fields and main-button options available.",
      ],
      [
        "2. Choose Your Page Photo",
        "Pick the main photo customers will see at the top of your TapToTable Page.",
      ],
      [
        form.campaign_type === "event"
          ? "3. Edit Your Event Page"
          : "3. Edit Before Claim",
        form.campaign_type === "event"
          ? "Add the event details and the main button customers see on the Event Page."
          : "Edit the promotion message, offer, and Claim Button customers see Before Claim.",
      ],
      [
        form.campaign_type === "event"
          ? "4. Choose What Happens After Button Tap"
          : "4. Edit After Claim",
        form.campaign_type === "event"
          ? "Choose what the main Event button opens. Website, Call, and Directions still appear automatically when available."
          : "Choose the Main Button After Claim. Website, Call, and Directions appear automatically when available.",
      ],
      [
        "5. Keep Restaurant Info Current",
        "Update your phone, address, website, menu link, and hours whenever anything changes.",
      ],
      [
        "Final Step. Save & Review",
        "Save your changes, then open your Live Page and check the full customer experience.",
      ],
    ].map(([title, description]) => (
      <div
        key={title}
        className="rounded-2xl border border-white/10 bg-black/20 p-4 sm:p-5"
      >
        <div className="text-sm font-semibold text-white">
          {title}
        </div>

        <p className="mt-2 text-sm leading-relaxed text-zinc-400">
          {description}
        </p>
      </div>
    ))}
  </div>



  <div className="mt-6 rounded-xl border border-amber-400/25 bg-amber-400/10 p-4">
    <div className="text-sm font-semibold text-amber-300">
      {form.campaign_type === "event"
        ? "Event Best Practice"
        : "Promotion Best Practice"}
    </div>
    <p className="mt-2 text-sm leading-relaxed text-zinc-300">
      {form.campaign_type === "event"
        ? "Make the event easy to understand at a glance. Include the event name, date, time, what customers can expect, and a clear action such as RSVP, Reserve Table, or Get Tickets."
        : "Give customers one clear reason to act. You can feature a menu item, seasonal special, complimentary add-on, loyalty reward, new menu launch, or another timely reason to visit without relying on a deep discount."}
    </p>
  </div>

  <div
    id="client-copy-paste-ideas"
    className="mt-6 scroll-mt-6 overflow-hidden rounded-2xl border border-violet-400/25 bg-violet-400/[0.05]"
  >
    <div className="border-b border-violet-400/15 px-5 py-4">
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-violet-300">
        Copy &amp; Paste Ideas
      </div>
      <h3 className="mt-2 text-lg font-semibold text-white">
        Ready-Made Promotion &amp; Event Copy
      </h3>
      <p className="mt-2 text-sm leading-relaxed text-zinc-400">
        Choose the type of wording you need, copy a ready-made line, then move
        back up and paste it into the matching field. Replace anything in
        [BRACKETS] with your own details.
      </p>
    </div>

    <div className="grid gap-5 p-5 lg:grid-cols-2">
      <div className="rounded-2xl border border-cyan-400/20 bg-cyan-400/[0.04] p-4">
        <div className="text-xs font-bold uppercase tracking-[0.16em] text-cyan-300">
          Promotion Copy Ideas
        </div>
        <p className="mt-1 text-xs leading-5 text-zinc-500">
          Use these for your promotion message, urgency, button text, or terms.
        </p>

        <label className="mt-4 mb-2 block text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-400">
          Choose What You Need
        </label>
        <select
          value={promotionCopyType}
          onChange={(e) => setPromotionCopyType(e.target.value)}
          className="w-full rounded-xl border border-cyan-400/30 bg-zinc-900 px-4 py-3 text-white focus:border-cyan-400 focus:outline-none"
        >
          {Object.keys(UNIVERSAL_PROMOTION_COPY).map((label) => (
            <option key={label} value={label}>
              {label}
            </option>
          ))}
        </select>

        <div className="mt-4 space-y-3">
          {UNIVERSAL_PROMOTION_COPY[promotionCopyType].map((idea, index) => {
            const key = `promotion-${promotionCopyType}-${index}`;
            return (
              <div
                key={key}
                className="rounded-xl border border-white/10 bg-black/25 p-4"
              >
                <p className="text-sm leading-relaxed text-zinc-300">
                  {idea}
                </p>
                <button
                  type="button"
                  onClick={() => copyResourceText(idea, key)}
                  className="mt-3 rounded-lg border border-cyan-400/30 bg-cyan-400/10 px-3 py-2 text-xs font-semibold text-cyan-200 hover:bg-cyan-400/15"
                >
                  {copiedResourceKey === key ? "✓ Copied" : "Copy This Line"}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-2xl border border-violet-400/20 bg-violet-400/[0.04] p-4">
        <div className="text-xs font-bold uppercase tracking-[0.16em] text-violet-300">
          Event Copy Ideas
        </div>
        <p className="mt-1 text-xs leading-5 text-zinc-500">
          Use these for your event message, urgency, button text, or event details.
        </p>

        <label className="mt-4 mb-2 block text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-400">
          Choose What You Need
        </label>
        <select
          value={eventCopyType}
          onChange={(e) => setEventCopyType(e.target.value)}
          className="w-full rounded-xl border border-violet-400/30 bg-zinc-900 px-4 py-3 text-white focus:border-violet-400 focus:outline-none"
        >
          {Object.keys(UNIVERSAL_EVENT_COPY).map((label) => (
            <option key={label} value={label}>
              {label}
            </option>
          ))}
        </select>

        <div className="mt-4 space-y-3">
          {UNIVERSAL_EVENT_COPY[eventCopyType].map((idea, index) => {
            const key = `event-${eventCopyType}-${index}`;
            return (
              <div
                key={key}
                className="rounded-xl border border-white/10 bg-black/25 p-4"
              >
                <p className="text-sm leading-relaxed text-zinc-300">
                  {idea}
                </p>
                <button
                  type="button"
                  onClick={() => copyResourceText(idea, key)}
                  className="mt-3 rounded-lg border border-violet-400/30 bg-violet-400/10 px-3 py-2 text-xs font-semibold text-violet-200 hover:bg-violet-400/15"
                >
                  {copiedResourceKey === key ? "✓ Copied" : "Copy This Line"}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  </div>

  <div className="mt-6 rounded-xl border border-green-400/20 bg-green-400/5 p-4">
    <div className="text-sm font-semibold text-green-300">
      Claim Codes &amp; Redemptions
    </div>
    <p className="mt-2 text-sm leading-relaxed text-zinc-300">
      Each customer claim receives its own claim code on the After-Claim Page.
      Ask the customer to show that code to the server or staff member when
      redeeming the offer. The code gives your team a simple way to recognize and
      keep track of individual redemptions if you choose to record them. If
      customers share your TapToTable Page, new visitors can claim the offer too,
      helping your promotion reach beyond the people who first received your link.
    </p>
  </div>

  </div>
</div>

{successCenter && (
  <div className="mt-10 overflow-hidden rounded-2xl border border-white/10 bg-white/5">
    <div className="border-b border-white/10 px-6 py-5">
      <div className="flex items-center gap-3">
        <div className="h-9 w-1 rounded-full bg-cyan-400" />
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-300">
            {successCenter.eyebrow}
          </p>

          <h2 className="mt-1 text-xl font-semibold text-white">
            {successCenter.title}
          </h2>
        </div>
      </div>

      <p className="mt-3 text-sm text-zinc-400">
        {successCenter.description}
      </p>
    </div>

    <div className="p-6">

    {successCenter.announcement_enabled && (
      <div className="mt-5 rounded-xl border border-amber-400/30 bg-amber-400/10 p-4">
        <div className="font-semibold text-amber-300">
          {successCenter.announcement_title}
        </div>

        <div className="mt-1 text-sm text-zinc-300">
          {successCenter.announcement_text}
        </div>
      </div>
    )}

    <div className="mt-5 grid gap-4 md:grid-cols-2">
      {(successCenter.resources || [])
        .filter((resource) => resource.enabled)
        .map((resource, index) => {
          const resourceUrl = successCenterResourceUrl(resource, form);
          const isMailto = resourceUrl.toLowerCase().startsWith("mailto:");

          return (
          <a
            key={`${resource.title}-${index}`}
            href={resourceUrl}
            target={
              !isMailto && resource.open_new_tab ? "_blank" : undefined
            }
            rel={
              !isMailto && resource.open_new_tab ? "noreferrer" : undefined
            }
            className="rounded-xl border border-white/10 bg-black/20 p-4 transition-colors hover:border-cyan-400/40 hover:bg-cyan-400/5"
          >
            <div className="font-semibold text-white">
              {resource.title}
            </div>

            <div className="mt-1 text-sm text-zinc-400">
              {resource.description}
            </div>

            <div className="mt-3 text-sm font-semibold text-cyan-300">
              {resource.button_text} →
            </div>
          </a>
          );
        })}
    </div>
    </div>
  </div>
)}
<div className="mt-10 pb-4">
  <button
    type="button"
    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
    className="min-h-[52px] w-full rounded-2xl border border-cyan-400/30 bg-cyan-400/10 px-5 py-3 text-sm font-bold text-cyan-200 transition-colors hover:bg-cyan-400/15"
  >
    ↑ Back to Top
  </button>
</div>
      </section>
    </main>
  );
}
function PortalPreview({ form, compact = false, screen = "primary" }) {
  const offerText =
    form.offer === "Custom..."
      ? form.custom_offer || "Your custom offer"
      : form.offer;

  const mainButtonText =
    form.cta_text === "Custom..."
      ? form.custom_cta_text || "View Deal"
      : form.cta_text || "View Deal";

  const afterClaimText = resolvedAfterClaimText(form);
  const isEvent = (form.campaign_type || "offer") === "event";
  const eventActionText = resolvedEventActionText(form);

  const ratingValue = Number(form.rating || 0);
  const reviewCount = Number(form.review_count || 0);
  const showRatingBadge =
    ratingValue >= 4.3 && reviewCount >= 25;

  const eventDateLabel = form.event_date
    ? new Date(`${form.event_date}T12:00:00`).toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "Event date";

  const utilityButtons = automaticUtilityButtons(form);
  const themeColor = customerThemeColor(form);

  return (
    <div
      className={`rounded-2xl border border-cyan-400/20 bg-black/30 ${
        compact ? "p-4" : "p-5"
      }`}
    >
      <div className="overflow-hidden rounded-2xl border border-white/10 bg-zinc-950">
        {form.hero_image_url ? (
          <img
            src={form.hero_image_url}
            alt=""
            className="h-40 w-full object-cover"
          />
        ) : (
          <div className="flex h-32 items-center justify-center bg-white/5 px-4 text-center text-sm text-zinc-500">
            Your page photo will appear here.
          </div>
        )}

        <div className="p-4">
          <h3 className="text-xl font-bold text-white">
            {form.restaurant_name || "Restaurant Name"}
          </h3>

          <div className="mt-2">
            {showRatingBadge ? (
              <span className="inline-flex rounded-full border border-amber-300/25 bg-amber-300/10 px-3 py-1 text-xs font-semibold text-amber-200">
                ★ {ratingValue.toFixed(1)} · {reviewCount.toLocaleString()} reviews
              </span>
            ) : (
              <span className="text-xs text-zinc-400">
                {form.address || "Restaurant details"}
              </span>
            )}
          </div>
        </div>
      </div>

      {isEvent ? (
        screen === "primary" ? (
          <div className="mt-4 rounded-2xl border border-violet-400/25 bg-zinc-950 p-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-violet-300">
              Event Page
            </p>

            <h3 className="mt-3 text-xl font-bold text-white">
              {form.event_name || "Your Event Name"}
            </h3>

            <div className="mt-3 flex flex-wrap gap-2 text-xs text-zinc-300">
              <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5">
                {eventDateLabel}
              </span>
              <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5">
                {form.event_time || "Event time"}
              </span>
            </div>

            <p className="mt-4 text-sm leading-5 text-zinc-300">
              {form.event_description ||
                "Your event description appears here."}
            </p>
          </div>
        ) : (
          <div className="mt-4 rounded-2xl border border-violet-400/25 bg-zinc-950 p-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-violet-300">
              After Button Tap
            </p>

            <div
              className="mt-4 rounded-xl border px-4 py-3 text-center text-sm font-semibold"
              style={{
                borderColor: `${themeColor}80`,
                backgroundColor: `${themeColor}22`,
                color: themeColor,
              }}
            >
              {eventActionText}
            </div>

            <p className="mt-2 text-center text-[11px] leading-4 text-zinc-500">
              {form.event_action_link
                ? "This button opens the destination you selected."
                : "No destination link is set yet."}
            </p>

            <div className="mt-3 grid min-w-0 grid-cols-3 gap-2 sm:gap-3">
              {utilityButtons.map((button) => (
                <div
                  key={button.label}
                  className="rounded-xl border border-white/10 bg-white/5 px-2 py-2.5 text-center text-xs font-semibold text-zinc-200"
                >
                  {button.label}
                </div>
              ))}
            </div>
          </div>
        )

      ) : form.campaign_type === "featured_item" ? (
        <div className="mt-4 rounded-2xl border border-cyan-400/25 bg-zinc-950 p-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-cyan-300">
            Featured Item Page
          </p>

          <h3 className="mt-3 text-xl font-bold text-white">
            {form.featured_item_name || "Your Featured Item"}
          </h3>

          <p className="mt-3 whitespace-pre-line text-sm leading-6 text-zinc-300">
            {form.featured_item_description ||
              "Your featured item description appears here."}
          </p>

          <div
            className="mt-5 flex min-h-[52px] items-center justify-center rounded-xl px-4 py-3 text-center text-sm font-bold"
            style={{
              backgroundColor: themeColor,
              color: contrastTextColor(themeColor),
            }}
          >
            {form.featured_action_text || "View Menu"}
          </div>

          <p className="mt-2 break-all text-center text-xs text-zinc-500">
            {form.featured_action_link || form.menu_url
              ? `Button destination: ${form.featured_action_link || form.menu_url}`
              : "Add a destination link or menu URL."}
          </p>

          <div className="mt-4 grid min-w-0 grid-cols-3 gap-2 sm:gap-3">
            {utilityButtons.map((button) => (
              <div
                key={button.label}
                className="rounded-xl border border-white/10 bg-white/5 px-2 py-2.5 text-center text-xs font-semibold text-zinc-200"
              >
                {button.label}
              </div>
            ))}
          </div>
        </div>
      ) : screen === "primary" ? (

        <div className="mt-4 rounded-2xl border border-white/10 bg-zinc-950 p-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-cyan-300">
            Before Claim Preview
          </p>

          <p className="mt-3 text-sm leading-5 text-zinc-300">
            {form.description ||
              "Your headline and description appear here."}
          </p>

          <div className="mt-4 rounded-xl border border-white/10 bg-white/5 p-4">
            <p className="text-xs uppercase tracking-wide text-zinc-500">
              Current Offer
            </p>

            <p className="mt-1 text-base font-semibold text-white">
              {offerText || "Your offer appears here."}
            </p>
          </div>

          <div
            className="mt-4 rounded-xl px-4 py-3 text-center text-sm font-bold text-black"
            style={{ backgroundColor: themeColor }}
          >
            {mainButtonText}
          </div>
        </div>
      ) : (
        <div className="mt-4 rounded-2xl border border-green-400/25 bg-zinc-950 p-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-green-300">
            After Claim Preview
          </p>

          <h3 className="mt-3 text-xl font-bold text-white">
            Offer Claimed
          </h3>

          <p className="mt-2 text-sm leading-5 text-zinc-300">
            This is what customers see after claiming your offer and receiving their code.
          </p>

          <div className="mt-5">
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-zinc-400">
              Main Button After Claim
            </p>
            <div
              className="flex min-h-[54px] w-full items-center justify-center rounded-xl px-5 py-3 text-center text-base font-bold"
              style={{
                backgroundColor: themeColor,
                color: contrastTextColor(themeColor),
                boxShadow: `0 12px 28px ${themeColor}33`,
              }}
            >
              {afterClaimText}
            </div>
          </div>

          <div className="mt-3 grid min-w-0 grid-cols-3 gap-2 sm:gap-3">
            {utilityButtons.map((button) => (
              <div
                key={button.label}
                className="rounded-xl border border-white/10 bg-white/5 px-2 py-2.5 text-center text-xs font-semibold text-zinc-200"
              >
                {button.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  textarea = false,
}) {
  return (
    <div>
      <label className="mb-2 flex items-center gap-2 text-sm text-zinc-300">
        {String(label || "").startsWith("→ ") ? (
          <>
            <span className="font-bold text-cyan-300" aria-hidden>
              →
            </span>
            <span>{String(label).slice(2)}</span>
          </>
        ) : (
          label
        )}
      </label>

      {textarea ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
          className="w-full rounded-xl bg-zinc-900 border border-zinc-700 px-4 py-3 text-white focus:outline-none focus:border-cyan-400"
        />
      ) : (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-xl bg-zinc-900 border border-zinc-700 px-4 py-3 text-white focus:outline-none focus:border-cyan-400"
        />
      )}
    </div>
  );
}
